import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ClipboardList, Plus, Trash2, CheckCircle, Calendar, Users,
    ChevronRight, ChevronUp, ChevronDown, X, Save, Send, BarChart3, FileText, AlertCircle,
    Star, ArrowLeft, Lock, Image, RefreshCw, Edit2, List, Sparkles,
    Copy, Download, Loader2, Layers, UserCheck, UserX, Printer, Search, Clock, History, Trophy, Link2, Phone
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../utils/axiosConfig';
import { io } from 'socket.io-client';
import InvitacionesExternasPanel from './encuestas/InvitacionesExternasPanel';

const TIPOS_PREGUNTA = [
    { id: 'texto_corto', nombre: 'Texto corto' },
    { id: 'texto_largo', nombre: 'Texto largo' },
    { id: 'opcion_unica', nombre: 'Opción única' },
    { id: 'opcion_multiple', nombre: 'Opción múltiple' },
    { id: 'escala', nombre: 'Escala (1-5)' },
    { id: 'matriz', nombre: 'Matriz (Opción única)' },
    { id: 'matriz_multiple', nombre: 'Matriz (Varias opciones)' },
];

const normalizeOpcion = (op) =>
    typeof op === 'string' ? { texto: op, imagen_url: null } : (op || { texto: '', imagen_url: null });

const tieneOpciones = (tipo) => ['opcion_unica', 'opcion_multiple', 'matriz', 'matriz_multiple'].includes(tipo);

const DEFAULT_SCALE_OPTIONS = ['1', '2', '3', '4', '5'];
const DEFAULT_MATRIZ_COLUMNAS = ['Muy malo (1)', 'Malo (2)', 'Ni bueno, ni malo (3)', 'Bueno (4)', 'Muy bueno (5)'];

const buildTextOptions = (items = DEFAULT_SCALE_OPTIONS) =>
    items.map(item => normalizeOpcion(item)).filter(op => op.texto !== undefined);

const getPreguntaScaleOptions = (pregunta) => {
    const opciones = pregunta?.opciones;
    if (Array.isArray(opciones)) {
        return buildTextOptions(opciones).filter(op => `${op.texto}`.trim() !== '');
    }
    if (opciones && typeof opciones === 'object' && !Array.isArray(opciones)) {
        return buildTextOptions(opciones.columnas || opciones.opciones || DEFAULT_SCALE_OPTIONS)
            .filter(op => `${op.texto}`.trim() !== '');
    }
    return buildTextOptions(DEFAULT_SCALE_OPTIONS);
};

const getPreguntaMatrizConfig = (pregunta) => {
    const opciones = pregunta?.opciones;
    if (opciones && typeof opciones === 'object' && !Array.isArray(opciones)) {
        return {
            criterios: buildTextOptions(opciones.criterios || opciones.opciones || []),
            columnas: buildTextOptions(opciones.columnas || DEFAULT_MATRIZ_COLUMNAS),
        };
    }
    return {
        criterios: buildTextOptions(Array.isArray(opciones) ? opciones : []),
        columnas: buildTextOptions(DEFAULT_MATRIZ_COLUMNAS),
    };
};

// Una matriz respondida es un objeto {criterioTexto: valor}, una clave por fila.
// Con una sola fila marcada ese objeto ya no es "vacío" para el chequeo genérico
// de obligatoriedad, así que hace falta validar fila por fila.
const isMatrizVacia = (pregunta, val) => {
    const { criterios } = getPreguntaMatrizConfig(pregunta);
    if (!criterios.length) return false;
    if (!val || typeof val !== 'object' || Array.isArray(val)) return true;
    return criterios.some(c => {
        const rowVal = val[c.texto];
        if (pregunta.tipo === 'matriz_multiple') return !Array.isArray(rowVal) || rowVal.length === 0;
        return rowVal === undefined || rowVal === null || rowVal === '';
    });
};

// Las respuestas de matriz/escala guardan la POSICIÓN de la columna (1..n), no un
// puntaje. Mostrar ese número crudo se lee al revés cuando las columnas van de
// mejor a peor, así que en resultados siempre se resuelve a la etiqueta.
const textoColumnaMatriz = (indice, columnas = []) => {
    const n = Number(indice);
    if (Number.isInteger(n) && n >= 1 && n <= columnas.length) {
        const txt = `${columnas[n - 1]?.texto ?? ''}`.trim();
        if (txt) return txt;
    }
    return `${indice}`;
};

// El backend arma los diccionarios de estadísticas en el orden correcto, pero al
// serializarlos a JSON las llaves salen ordenadas alfabéticamente. Por eso NUNCA
// hay que confiar en el orden de las llaves de un objeto para mostrar criterios,
// columnas u opciones: siempre se manda un arreglo de referencia aparte.
const textosDe = (items = []) => (Array.isArray(items) ? items : [])
    .map(it => `${(typeof it === 'string' ? it : it?.texto) ?? ''}`.trim())
    .filter(Boolean);

// Une el orden de referencia con las llaves que realmente traen datos: primero la
// referencia completa (aunque un criterio no tenga respuestas, para no ocultarlo)
// y al final cualquier llave suelta que la referencia no contemplaba.
const combinarOrden = (referencia = [], claves = []) => {
    const vistos = new Set();
    const salida = [];
    [...referencia, ...claves].forEach(txt => {
        if (txt && !vistos.has(txt)) {
            vistos.add(txt);
            salida.push(txt);
        }
    });
    return salida;
};

// Criterios de una matriz en su orden original: primero el arreglo que manda el
// backend (`stats.criterios`), si no la configuración de la pregunta y, como
// último recurso, las llaves del objeto de datos (ya alfabetizadas).
const criteriosEnOrden = (stats, pregunta, datos) => {
    const desdeStats = textosDe(stats?.criterios);
    const referencia = desdeStats.length ? desdeStats : textosDe(getPreguntaMatrizConfig(pregunta).criterios);
    return combinarOrden(referencia, Object.keys(datos || {}));
};

// Columnas (escala) de una matriz en su orden original, como objetos {texto,...}
// para poder pasárselas a textoColumnaMatriz.
const columnasEnOrden = (stats, pregunta) => {
    const desdeStats = textosDe(stats?.columnas);
    return desdeStats.length ? buildTextOptions(desdeStats) : getPreguntaMatrizConfig(pregunta).columnas;
};

// Opciones de una pregunta de selección en el orden en que se definieron.
const opcionesEnOrden = (pregunta, conteo) => combinarOrden(
    textosDe(Array.isArray(pregunta?.opciones) ? pregunta.opciones : []),
    Object.keys(conteo || {})
);

// Preguntas agrupadas por sección (secciones por `orden`, preguntas por `orden`
// dentro de cada grupo), igual que se ven al responder la encuesta.
const agruparPreguntasPorSeccion = (preguntas = [], secciones = []) => {
    const porOrden = (a, b) => (a.orden || 0) - (b.orden || 0);
    const asignadas = new Set();
    const grupos = [{
        seccion: null,
        preguntas: preguntas.filter(p => !p.seccionId).sort(porOrden),
    }];
    grupos[0].preguntas.forEach(p => asignadas.add(p.id));

    [...secciones].sort(porOrden).forEach(sec => {
        const delGrupo = preguntas.filter(p => String(p.seccionId) === String(sec.id)).sort(porOrden);
        delGrupo.forEach(p => asignadas.add(p.id));
        if (delGrupo.length > 0) grupos.push({ seccion: sec, preguntas: delGrupo });
    });

    // Preguntas cuya sección ya no existe: no se pierden, van al final.
    const huerfanas = preguntas.filter(p => !asignadas.has(p.id)).sort(porOrden);
    if (huerfanas.length > 0) grupos.push({ seccion: null, preguntas: huerfanas });

    return grupos.filter(g => g.preguntas.length > 0);
};

const sinAcentos = (txt) => `${txt}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

const ETIQUETAS_ALTAS = ['muy bueno', 'muy buena', 'excelente', 'totalmente de acuerdo', 'siempre'];
const ETIQUETAS_BAJAS = ['muy malo', 'muy mala', 'pesimo', 'pesima', 'nunca', 'totalmente en desacuerdo'];

const empiezaPor = (txt, lista) => lista.some(p => sinAcentos(txt).startsWith(p));

// La posición de la columna es el valor que se guarda, así que la escala tiene que
// ir de menor a mayor. Detecta el caso invertido para avisarlo en el editor.
const escalaEstaInvertida = (columnas = []) => {
    const textos = columnas.map(c => `${c?.texto ?? ''}`.trim()).filter(Boolean);
    if (textos.length < 2) return false;

    // Etiquetas que traen su valor, como "Muy bueno (5)" o "5"
    const numeros = textos.map(t => {
        const m = t.match(/\((\d+)\)/) || t.match(/^(\d+)$/);
        return m ? parseInt(m[1], 10) : null;
    });
    if (numeros.every(n => n !== null)) {
        return numeros.every((n, i) => i === 0 || n < numeros[i - 1]);
    }

    // Sin números: mirar la polaridad de los extremos
    return empiezaPor(textos[0], ETIQUETAS_ALTAS) && empiezaPor(textos[textos.length - 1], ETIQUETAS_BAJAS);
};

const normalizePreguntaOpciones = (tipo, opciones) => {
    if (tipo === 'escala') {
        const columnas = getPreguntaScaleOptions({ opciones });
        return columnas.length > 0 ? columnas : buildTextOptions(DEFAULT_SCALE_OPTIONS);
    }
    if (tipo === 'matriz' || tipo === 'matriz_multiple') {
        const { criterios, columnas } = getPreguntaMatrizConfig({ opciones });
        return {
            criterios: criterios.length > 0 ? criterios : [{ texto: '', imagen_url: null }],
            columnas: columnas.length > 0 ? columnas : buildTextOptions(DEFAULT_MATRIZ_COLUMNAS),
        };
    }
    if (Array.isArray(opciones)) {
        return opciones.map(normalizeOpcion);
    }
    return [{ texto: '', imagen_url: null }, { texto: '', imagen_url: null }];
};

const serializePreguntaOpciones = (tipo, opciones) => {
    if (tipo === 'matriz' || tipo === 'matriz_multiple') {
        const config = getPreguntaMatrizConfig({ opciones });
        return {
            criterios: config.criterios.map(normalizeOpcion),
            columnas: config.columnas.map(normalizeOpcion),
        };
    }
    if (tipo === 'escala') {
        return getPreguntaScaleOptions({ opciones }).map(normalizeOpcion);
    }
    return (Array.isArray(opciones) ? opciones : []).map(normalizeOpcion);
};

// ---- ColumnasEditor ----
// Cada fila lleva su posición porque eso es lo que se guarda en la respuesta:
// marcando los extremos (menor/mayor) la escala no se puede cargar al revés sin verlo.
const ColumnasEditor = ({ columnas, titulo, ayuda, placeholder, onUpdate, onRemove, onAdd, onInvertir, textoAgregar }) => {
    const invertida = escalaEstaInvertida(columnas);
    const ultima = columnas.length - 1;

    return (
        <div className="space-y-2 pt-1">
            <div className="flex items-baseline justify-between gap-3">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{titulo}</p>
                {columnas.length > 1 && !invertida && (
                    <button type="button" onClick={onInvertir}
                        className="text-xs text-gray-400 hover:text-teal-600 transition-colors">
                        Invertir orden
                    </button>
                )}
            </div>
            <p className="text-xs text-gray-400 -mt-1">{ayuda}</p>

            {invertida && (
                <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-amber-900">La escala está al revés</p>
                        <p className="text-xs text-amber-700 mt-0.5">
                            La última posición es la calificación más alta. Así como está, quien marque la mejor opción queda registrado con el valor más bajo.
                        </p>
                        <button type="button" onClick={onInvertir}
                            className="mt-2 px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 transition-colors">
                            Invertir orden
                        </button>
                    </div>
                </div>
            )}

            {columnas.map((col, oi) => (
                <div key={oi} className="flex items-center gap-2">
                    <span className="flex items-center gap-1.5 w-20 flex-shrink-0">
                        <span className="w-6 h-6 rounded-md bg-gray-100 text-gray-500 text-xs font-semibold flex items-center justify-center tabular-nums">{oi + 1}</span>
                        {oi === 0 && <span className="text-[10px] uppercase tracking-wide text-gray-400">menor</span>}
                        {oi === ultima && oi !== 0 && <span className="text-[10px] uppercase tracking-wide text-teal-600 font-semibold">mayor</span>}
                    </span>
                    <input type="text" value={col.texto} onChange={e => onUpdate(oi, 'texto', e.target.value)}
                        className="flex-1 px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder={`${placeholder} ${oi + 1}`} />
                    <button onClick={() => onRemove(oi)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><X size={15} /></button>
                </div>
            ))}
            <button type="button" onClick={onAdd} className="flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-700 mt-1">
                <Plus size={15} />{textoAgregar}
            </button>
        </div>
    );
};

const OTRO_SENTINEL = '__otro__';
const newClientKey = () => `k${Date.now()}${Math.random().toString(36).slice(2, 8)}`;

// ---- ImageUploadButton ----
const ImageUploadButton = ({ value, onChange, small = false, height = 'h-44', buttonMode = false }) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const handleChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('imagen', file);
            const res = await axiosInstance.post('/api/encuestas-producto/upload-imagen', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            onChange(res.data.url);
        } catch (err) {
            console.error('Error subiendo imagen', err);
        } finally {
            setUploading(false);
        }
        e.target.value = '';
    };

    if (value) {
        return (
            <div className={`relative ${small ? 'w-14 h-14' : `w-full ${height}`} rounded-xl overflow-hidden border border-gray-200 group cursor-pointer flex-shrink-0`}>
                <img src={value} alt="preview" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button type="button" onClick={() => inputRef.current?.click()} className="p-1.5 bg-white rounded-lg shadow">
                        <Edit2 size={13} className="text-gray-700" />
                    </button>
                    <button type="button" onClick={() => onChange(null)} className="p-1.5 bg-white rounded-lg shadow">
                        <X size={13} className="text-red-500" />
                    </button>
                </div>
                <input ref={inputRef} type="file" accept="image/*" onChange={handleChange} hidden />
            </div>
        );
    }

    return (
        <>
            <input ref={inputRef} type="file" accept="image/*" onChange={handleChange} hidden />
            {buttonMode ? (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors flex items-center justify-center"
                    title="Añadir imagen"
                >
                    {uploading ? <RefreshCw size={16} className="animate-spin" /> : <Image size={16} />}
                </button>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className={`${small ? 'w-14 h-14' : `w-full ${height}`} flex-shrink-0 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-2 text-gray-400 hover:border-teal-400 hover:text-teal-500 transition-colors`}
                >
                    {uploading

                    ? <RefreshCw size={small ? 16 : 20} className="animate-spin" />
                    : <Image size={small ? 16 : 20} />
                }
                {!small && <span className="text-xs font-medium">Añadir imagen</span>}
                </button>
            )}
        </>
    );
};

// ---- EmpleadoSelector ----
// Pildora de filtro rapido (genero / sede / jerarquia).
const FiltroPill = ({ activo, onClick, disabled, title, children }) => (
    <button
        type="button" onClick={onClick} disabled={disabled} title={title}
        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${activo
            ? 'bg-teal-600 border-teal-600 text-white'
            : 'bg-white border-gray-200 text-gray-600 hover:border-teal-300 hover:text-teal-700'
            } ${disabled ? 'cursor-default' : ''}`}
    >
        {children}
    </button>
);

// El cargo de Presidencia (nivel 10) queda fuera de cualquier seleccion por
// filtro: solo se le puede invitar buscandolo a mano por su nombre. El nombre
// del cargo es el respaldo solo si el cargo no tiene nivel cargado (con nivel,
// "Asistente de Presidencia" no es Presidencia), y se descarta "vice" para no
// sacar por error a un vicepresidente.
const esPresidencia = (emp) => {
    if (emp.cargo_nivel != null) return emp.cargo_nivel === 10;
    const cargo = (emp.cargo || '').toLowerCase();
    return cargo.includes('presiden') && !cargo.includes('vice');
};

// A partir de aqui la lista de chips tapa el formulario: se resume en una linea.
const MAX_CHIPS_VISIBLES = 12;

const EmpleadoSelector = ({ selectedIds, onChange }) => {
    const [empleados, setEmpleados] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [genero, setGenero] = useState(null);   // null | 'M' | 'F'
    const [sede, setSede] = useState(null);       // null | nombre de la sede
    const [desdeGG, setDesdeGG] = useState(false);
    const [verSeleccion, setVerSeleccion] = useState(false);
    // Al editar una encuesta, los invitados que ya tenia cuentan como manuales:
    // si no, el primer filtro que se toque se los llevaria por delante.
    const [manual, setManual] = useState(selectedIds);
    const [excluidos, setExcluidos] = useState([]);  // quitados a dedo

    useEffect(() => {
        axiosInstance.get('/api/empleados?estado=Activo')
            .then(r => setEmpleados(r.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    // Sedes reales de la nomina, sin quemarlas en el codigo.
    const sedes = useMemo(
        () => [...new Set(empleados.map(e => e.sede).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
        [empleados]
    );

    // Elegir genero o sede implica "de Gerente General hacia abajo": la cupula
    // nunca entra por filtro, aunque no se haya pulsado ese boton.
    const jerarquiaActiva = desdeGG || !!genero || !!sede;
    const hayFiltros = !!genero || !!sede || desdeGG;

    const coincide = (e, g, s, jerarquia) =>
        (!g || e.genero === g) &&
        (!s || e.sede === s) &&
        (!jerarquia || !esPresidencia(e));

    // La seleccion se recalcula entera cada vez que cambia algo: lo que traen
    // los filtros activos, mas los agregados a mano, menos los quitados a mano.
    // Asi apagar un filtro se lleva a su gente, pero respeta lo tocado a dedo.
    const recalcular = (g, s, gg, man, exc) => {
        const jerarquia = gg || !!g || !!s;
        const hay = !!g || !!s || gg;
        const ids = new Set(hay ? empleados.filter(e => coincide(e, g, s, jerarquia)).map(e => e.id) : []);
        man.forEach(id => ids.add(id));
        exc.forEach(id => ids.delete(id));
        onChange([...ids]);
    };

    const toggleGenero = (v) => { const g = genero === v ? null : v; setGenero(g); recalcular(g, sede, desdeGG, manual, excluidos); };
    const toggleSede = (v) => { const x = sede === v ? null : v; setSede(x); recalcular(genero, x, desdeGG, manual, excluidos); };
    const toggleDesdeGG = () => { const gg = !desdeGG; setDesdeGG(gg); recalcular(genero, sede, gg, manual, excluidos); };

    const limpiarFiltros = () => {
        setGenero(null); setSede(null); setDesdeGG(false);
        recalcular(null, null, false, manual, excluidos);
    };

    const coincidencias = useMemo(
        () => (hayFiltros ? empleados.filter(e => coincide(e, genero, sede, jerarquiaActiva)).length : 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [empleados, genero, sede, jerarquiaActiva, hayFiltros]
    );

    // La lista de abajo no se recorta con los filtros: asi se puede seguir
    // buscando a mano a quien sea, Presidencia incluida.
    const filtered = empleados
        .filter(e =>
            e.nombre?.toLowerCase().includes(search.toLowerCase()) ||
            e.cargo?.toLowerCase().includes(search.toLowerCase()) ||
            e.departamento?.toLowerCase().includes(search.toLowerCase())
        )
        .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));

    // Marcar o desmarcar a alguien cuenta como decision manual y sobrevive a
    // los cambios de filtro (asi se invita a Presidencia buscandola por nombre).
    const toggle = (id) => {
        const quitando = selectedIds.includes(id);
        const man = quitando ? manual.filter(x => x !== id) : [...manual.filter(x => x !== id), id];
        const exc = quitando ? [...excluidos.filter(x => x !== id), id] : excluidos.filter(x => x !== id);
        setManual(man); setExcluidos(exc);
        recalcular(genero, sede, desdeGG, man, exc);
    };

    const seleccionarBuscados = () => {
        const ids = filtered.map(e => e.id);
        const man = [...new Set([...manual, ...ids])];
        const exc = excluidos.filter(x => !ids.includes(x));
        setManual(man); setExcluidos(exc);
        recalcular(genero, sede, desdeGG, man, exc);
    };

    const deseleccionarTodo = () => {
        setGenero(null); setSede(null); setDesdeGG(false);
        setManual([]); setExcluidos([]);
        onChange([]);
    };

    const selected = empleados.filter(e => selectedIds.includes(e.id));

    return (
        <div className="space-y-3">
            {selected.length > 0 && (
                <div className="space-y-2">
                    {/* Con una seleccion por filtro son cientos de chips: se resumen */}
                    {selected.length > MAX_CHIPS_VISIBLES && (
                        <div className="flex items-center gap-2 text-xs">
                            <span className="text-gray-500">{selected.length} empleados seleccionados</span>
                            <button type="button" onClick={() => setVerSeleccion(v => !v)} className="text-teal-600 hover:text-teal-700 font-medium">
                                {verSeleccion ? 'Ocultar lista' : 'Ver lista'}
                            </button>
                        </div>
                    )}
                    {(selected.length <= MAX_CHIPS_VISIBLES || verSeleccion) && (
                        <div className="flex flex-wrap gap-2 max-h-40 overflow-auto">
                            {selected.map(emp => (
                                <span key={emp.id} className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-full text-sm">
                                    {emp.nombre}
                                    <button type="button" onClick={() => toggle(emp.id)} className="hover:text-teal-900 ml-0.5"><X size={13} /></button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Filtros rapidos: escogen la seleccion de golpe */}
            <div className="space-y-1.5 pb-1">
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-gray-400 w-12 shrink-0">Género</span>
                    <FiltroPill activo={genero === 'M'} disabled={loading} onClick={() => toggleGenero('M')}>Hombres</FiltroPill>
                    <FiltroPill activo={genero === 'F'} disabled={loading} onClick={() => toggleGenero('F')}>Mujeres</FiltroPill>
                </div>
                {sedes.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-xs text-gray-400 w-12 shrink-0">Sede</span>
                        {sedes.map(s => (
                            <FiltroPill key={s} activo={sede === s} disabled={loading} onClick={() => toggleSede(s)}>{s}</FiltroPill>
                        ))}
                    </div>
                )}
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-gray-400 w-12 shrink-0">Cargo</span>
                    <FiltroPill
                        activo={jerarquiaActiva}
                        disabled={loading || (!desdeGG && jerarquiaActiva)}
                        title={!desdeGG && jerarquiaActiva ? 'Los filtros nunca incluyen a Presidencia' : 'Todos menos Presidencia'}
                        onClick={toggleDesdeGG}
                    >
                        Desde Gerente General
                    </FiltroPill>
                    {hayFiltros && (
                        <button type="button" onClick={limpiarFiltros} className="ml-auto text-xs text-gray-400 hover:text-gray-600">Limpiar filtros</button>
                    )}
                </div>
                {hayFiltros && !loading && (
                    <p className="text-xs text-gray-400 pt-0.5">
                        {coincidencias} {coincidencias === 1 ? 'empleado coincide' : 'empleados coinciden'} · Presidencia solo entra buscándola por nombre
                    </p>
                )}
            </div>

            <input
                type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Buscar empleados..."
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-500 text-sm"
            />
            <div className="flex items-center gap-3 text-sm">
                <button type="button" onClick={seleccionarBuscados} className="text-teal-600 hover:text-teal-700 font-medium">Seleccionar todos</button>
                <span className="text-gray-300">·</span>
                <button type="button" onClick={deseleccionarTodo} className="text-gray-500 hover:text-gray-700">Deseleccionar</button>
                <span className="ml-auto text-gray-400">{selectedIds.length} seleccionados</span>
            </div>
            <div className="max-h-52 overflow-auto border border-gray-200 rounded-xl divide-y divide-gray-100">
                {loading ? (
                    <div className="p-4 text-center text-gray-400 text-sm">Cargando...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-4 text-center text-gray-400 text-sm">Sin resultados</div>
                ) : filtered.map(emp => (
                    <label key={emp.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer">
                        <input type="checkbox" checked={selectedIds.includes(emp.id)} onChange={() => toggle(emp.id)} className="accent-teal-600 w-4 h-4" />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{emp.nombre}</p>
                            <p className="text-xs text-gray-400 truncate">{emp.cargo || emp.departamento}</p>
                        </div>
                    </label>
                ))}
            </div>
        </div>
    );
};

// ---- PreguntaRespuesta ----
// Soporta "Otro" en opcion_unica/multiple: el valor guardado es { sel, otro }.
export const PreguntaRespuesta = ({ pregunta, value, onChange }) => {
    const opciones = (Array.isArray(pregunta.opciones) ? pregunta.opciones : []).map(normalizeOpcion);
    const escalaOpciones = getPreguntaScaleOptions(pregunta);
    const matrizConfig = getPreguntaMatrizConfig(pregunta);

    const permiteOtro = pregunta.permiteOtro && ['opcion_unica', 'opcion_multiple'].includes(pregunta.tipo);

    // Normalizar value para opciones con "Otro": {sel, otro}
    const current = permiteOtro
        ? (value && typeof value === 'object' && !Array.isArray(value) ? value : { sel: value ?? (pregunta.tipo === 'opcion_multiple' ? [] : ''), otro: '' })
        : value;

    const setSel = (newSel) => {
        if (permiteOtro) onChange({ ...current, sel: newSel });
        else onChange(newSel);
    };
    const setOtro = (txt) => onChange({ ...current, otro: txt });

    const sel = permiteOtro ? current.sel : value;
    const otroActivo = permiteOtro && (
        pregunta.tipo === 'opcion_multiple'
            ? (Array.isArray(sel) && sel.includes(OTRO_SENTINEL))
            : sel === OTRO_SENTINEL
    );

    const toggleMultiple = (texto) => {
        const arr = Array.isArray(sel) ? sel : [];
        setSel(arr.includes(texto) ? arr.filter(v => v !== texto) : [...arr, texto]);
    };

    const toggleMultipleIdx = (idx) => {
        const arr = Array.isArray(sel) ? sel : [];
        setSel(arr.includes(idx) ? arr.filter(v => v !== idx) : [...arr, idx]);
    };



    const renderInput = () => {
        switch (pregunta.tipo) {
            case 'texto_corto':
                return <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder="Tu respuesta..." className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-500 text-sm" />;
            case 'texto_largo':
                return <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder="Tu respuesta..." rows={4} className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-500 text-sm resize-none" />;
            case 'opcion_unica':
                return (
                    <div className="space-y-2">
                        {opciones.map((op, i) => (
                            <label key={i} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
                                <input type="radio" name={`p-${pregunta.id}`} value={op.texto} checked={sel === op.texto} onChange={() => setSel(op.texto)} className="accent-teal-600 w-4 h-4" />
                                <span className="text-sm text-gray-700">{op.texto}</span>
                            </label>
                        ))}
                        {permiteOtro && (
                            <div className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors">
                                <input type="radio" name={`p-${pregunta.id}`} checked={sel === OTRO_SENTINEL} onChange={() => setSel(OTRO_SENTINEL)} className="accent-teal-600 w-4 h-4" />
                                <span className="text-sm text-gray-700">Otro</span>
                                {otroActivo && (
                                    <input
                                        type="text" value={current.otro || ''} onChange={e => setOtro(e.target.value)}
                                        placeholder="Especifica..."
                                        className="flex-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-500"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                );
            case 'opcion_multiple':
                return (
                    <div className="space-y-2">
                        {opciones.map((op, i) => (
                            <label key={i} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
                                <input type="checkbox" checked={Array.isArray(sel) && sel.includes(op.texto)} onChange={() => toggleMultiple(op.texto)} className="accent-teal-600 w-4 h-4" />
                                <span className="text-sm text-gray-700">{op.texto}</span>
                            </label>
                        ))}
                        {permiteOtro && (
                            <div className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors">
                                <input type="checkbox" checked={Array.isArray(sel) && sel.includes(OTRO_SENTINEL)} onChange={() => toggleMultiple(OTRO_SENTINEL)} className="accent-teal-600 w-4 h-4" />
                                <span className="text-sm text-gray-700">Otro</span>
                                {otroActivo && (
                                    <input
                                        type="text" value={current.otro || ''} onChange={e => setOtro(e.target.value)}
                                        placeholder="Especifica..."
                                        className="flex-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-500"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                );
            case 'escala':
                return (
                    <div className="flex items-center justify-center gap-2 py-2">
                        {escalaOpciones.map((op, idx) => {
                            const valor = idx + 1;
                            return (
                            <button key={`${op.texto}-${valor}`} type="button" onClick={() => onChange(valor)}
                                className={`min-w-12 h-12 px-3 rounded-xl border-2 text-sm font-semibold transition-all ${value === valor ? 'bg-teal-600 border-teal-600 text-white' : 'border-gray-200 text-gray-500 hover:border-teal-400'}`}>
                                {op.texto}
                            </button>
                            );
                        })}
                    </div>
                );
            case 'matriz':
            case 'matriz_multiple':
                return (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr>
                                    <th className="text-left py-2 pr-4 text-gray-500 font-medium w-48">Criterio</th>
                                    {matrizConfig.columnas.map((col, idx) => <th key={`${col.texto}-${idx}`} className="text-center py-2 px-3 text-gray-500 font-medium min-w-16">{col.texto}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {matrizConfig.criterios.map((op, i) => {
                                    const rawVal = value?.[op.texto];
                                    const arrValue = Array.isArray(rawVal) ? rawVal : (rawVal ? [rawVal] : []);
                                    return (
                                    <tr key={i} className="border-t border-gray-100">
                                        <td className="py-3 pr-4 text-gray-700">{op.texto}</td>
                                        {matrizConfig.columnas.map((col, idx) => {
                                            const valor = idx + 1;
                                            const isChecked = pregunta.tipo === 'matriz_multiple' 
                                                ? arrValue.includes(valor) 
                                                : rawVal === valor;
                                            return (
                                            <td key={`${col.texto}-${valor}`} className="text-center py-3 px-3">
                                                <input 
                                                    type={pregunta.tipo === 'matriz_multiple' ? 'checkbox' : 'radio'} 
                                                    name={`p-${pregunta.id}-${op.texto}`} 
                                                    checked={isChecked} 
                                                    onChange={(e) => {
                                                        if (pregunta.tipo === 'matriz_multiple') {
                                                            const newVal = e.target.checked 
                                                                ? [...arrValue, valor] 
                                                                : arrValue.filter(v => v !== valor);
                                                            onChange({ ...(value || {}), [op.texto]: newVal });
                                                        } else {
                                                            onChange({ ...(value || {}), [op.texto]: valor });
                                                        }
                                                    }} 
                                                    className="accent-teal-600 w-4 h-4 cursor-pointer" 
                                                />
                                            </td>
                                            );
                                        })}
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                );
            default:
                return <p className="text-gray-400 text-sm">Tipo no soportado</p>;
        }
    };

    return (
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-start gap-2 mb-4">
                {pregunta.obligatoria && <span className="text-red-500 mt-0.5 text-sm">*</span>}
                <h3 className="font-medium text-gray-900 leading-snug">{pregunta.texto}</h3>
            </div>
            {pregunta.imagenUrl && (
                <div className="mb-6 rounded-xl overflow-hidden border border-gray-100 flex justify-center bg-gray-50">
                    <img src={pregunta.imagenUrl} alt="Imagen de pregunta" className="max-h-80 object-contain" />
                </div>
            )}
            {renderInput()}
        </div>
    );
};

// Calcula qué grupos (secciones) y preguntas son visibles según lógica de salto.
// Oculta por defecto las secciones que son "condicionales" (destino de algún salto)
// a menos que se llegue a ellas a través de sus respuestas. Pura: la usan la vista
// interna y la página de invitados externos (EncuestaInvitadoView).
export const calcularVisibilidadEncuestaPura = (encuesta, respuestasActuales) => {
    const preguntasActivas = (encuesta?.preguntas || []).filter(p => !p.archivada);
    const secciones = encuesta?.secciones || [];
    
    const allGroups = [
        { seccion: null, key: 'none' },
        ...secciones.map(s => ({ seccion: s, key: String(s.id) || String(s.clientKey) })),
    ];

    if (secciones.length === 0) {
        return {
            gruposVisibles: allGroups,
            preguntasVisibles: new Set(preguntasActivas.map(p => p.id)),
        };
    }

    // 1. Identificar secciones condicionales (destino de algún salto)
    const seccionesCondicionales = new Set();
    for (const preg of preguntasActivas) {
        if (!['opcion_unica', 'opcion_multiple'].includes(preg.tipo)) continue;
        const salto = preg.logicaSalto;
        if (!salto) continue;
        for (const key in salto) {
            const destino = salto[key];
            if (destino && destino !== 'ENVIAR') {
                seccionesCondicionales.add(String(destino));
            }
        }
    }

    const visiblesSet = new Set();
    let truncarDesdeIndex = -1;

    // Agregar los grupos que NO son condicionales (flujo regular)
    for (const g of allGroups) {
        if (g.seccion && seccionesCondicionales.has(String(g.seccion.id))) continue;
        visiblesSet.add(g.key);
    }

    // 2. Evaluar respuestas y activar saltos
    for (let idx = 0; idx < allGroups.length; idx++) {
        const g = allGroups[idx];
        if (!visiblesSet.has(g.key)) continue;

        const preguntasGrupo = preguntasActivas.filter(p =>
            g.seccion ? String(p.seccionId) === String(g.seccion.id) : !p.seccionId
        );

        let saltoDestino = null;
        for (const preg of preguntasGrupo) {
            if (!['opcion_unica', 'opcion_multiple'].includes(preg.tipo)) continue;
            const salto = preg.logicaSalto;
            if (!salto || Object.keys(salto).length === 0) continue;

            const respuestaActual = respuestasActuales[preg.id];
            if (respuestaActual === undefined || respuestaActual === null || respuestaActual === '') continue;

            let jumpKeys = [];
            if (Array.isArray(respuestaActual)) {
                jumpKeys = respuestaActual.map(String);
            } else {
                jumpKeys = [String(respuestaActual)];
            }

            for (const key of jumpKeys) {
                const destino = salto[key];
                if (destino) {
                    saltoDestino = destino;
                    break;
                }
            }
            if (saltoDestino) break; // Tomar el primer salto efectivo de la sección
        }
        
        if (saltoDestino === 'ENVIAR') {
            truncarDesdeIndex = idx + 1;
            break;
        } else if (saltoDestino) {
            visiblesSet.add(String(saltoDestino));
            // Ocultar cualquier grupo intermedio
            const destinoIdx = allGroups.findIndex(ag => ag.seccion && String(ag.seccion.id) === String(saltoDestino));
            if (destinoIdx !== -1) {
                for (let i = idx + 1; i < destinoIdx; i++) {
                    visiblesSet.delete(allGroups[i].key);
                }
            }
        }
    }
    
    if (truncarDesdeIndex !== -1) {
         for (let i = truncarDesdeIndex; i < allGroups.length; i++) {
             visiblesSet.delete(allGroups[i].key);
         }
    }

    const gruposVisibles = allGroups.filter(g => visiblesSet.has(g.key));
    const preguntasVisibles = new Set();
    
    for (const p of preguntasActivas) {
        const key = p.seccionId ? String(p.seccionId) : 'none';
        if (visiblesSet.has(key)) preguntasVisibles.add(p.id);
    }

    return { gruposVisibles, preguntasVisibles };
};

// Valida las obligatorias de `preguntas` (ya filtradas a las visibles).
// Devuelve el mensaje de error o null. Compartida con EncuestaInvitadoView.
export const validarObligatorias = (preguntas, respuestas) => {
    for (const p of preguntas) {
        if (!p.obligatoria) continue;
        const val = respuestas[p.id];
        if (['matriz', 'matriz_multiple'].includes(p.tipo)) {
            if (isMatrizVacia(p, val)) {
                return `La pregunta "${p.texto.slice(0, 60)}..." es obligatoria`;
            }
            continue;
        }
        const permiteOtro = p.permiteOtro && ['opcion_unica', 'opcion_multiple'].includes(p.tipo);
        let isEmpty;
        if (permiteOtro && val && typeof val === 'object' && !Array.isArray(val)) {
            const sel = val.sel;
            const selEmpty = sel === undefined || sel === null || sel === '' || (Array.isArray(sel) && sel.length === 0);
            // si solo eligió "Otro", requiere texto
            const onlyOtro = Array.isArray(sel)
                ? sel.length > 0 && sel.every(s => s === OTRO_SENTINEL)
                : sel === OTRO_SENTINEL;
            isEmpty = selEmpty || (onlyOtro && !(val.otro || '').trim());
        } else {
            isEmpty = val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);
            if (isEmpty && val === 0) isEmpty = false;
        }
        if (isEmpty) {
            return `La pregunta "${p.texto.slice(0, 60)}..." es obligatoria`;
        }
    }

    return null;
};

// Pasa las respuestas al formato que espera el backend (índices de opciones
// con imagen -> texto, "Otro" -> {sel, otro}). Compartida con EncuestaInvitadoView.
export const convertirRespuestasParaEnvio = (preguntas, respuestas) => {
    // Convertir índices de opciones con imágenes a texto para el backend
    const finalRespuestas = {};
    for (const [pid, val] of Object.entries(respuestas)) {
        const preg = preguntas.find(p => String(p.id) === String(pid));
        const opciones = (Array.isArray(preg?.opciones) ? preg.opciones : []).map(normalizeOpcion);
        const hasImg = opciones.some(op => op.imagen_url);
        const permiteOtro = preg?.permiteOtro && ['opcion_unica', 'opcion_multiple'].includes(preg.tipo);

        const resolveIdxToTxt = (v) => typeof v === 'number' ? (opciones[v]?.texto || v) : v;

        if (permiteOtro && val && typeof val === 'object' && !Array.isArray(val)) {
            let sel = val.sel;
            if (hasImg) {
                sel = Array.isArray(sel) ? sel.map(resolveIdxToTxt) : resolveIdxToTxt(sel);
            }
            finalRespuestas[pid] = { sel, otro: (val.otro || '').trim() };
        } else if (preg && hasImg && ['opcion_unica', 'opcion_multiple'].includes(preg.tipo)) {
            if (Array.isArray(val)) {
                finalRespuestas[pid] = val.map(resolveIdxToTxt);
            } else if (typeof val === 'number') {
                finalRespuestas[pid] = opciones[val]?.texto || val;
            } else {
                finalRespuestas[pid] = val;
            }
        } else {
            finalRespuestas[pid] = val;
        }
    }

    return finalRespuestas;
};

// ---- SeccionHeader ----
export const SeccionHeader = ({ seccion }) => (
    <div className="pt-2">
        <div className="flex items-center gap-2 mb-1">
            <div className="h-[2px] flex-1 bg-gradient-to-r from-teal-200 to-transparent" />
            <span className="px-3 text-xs font-semibold text-teal-700 uppercase tracking-wider">{seccion.titulo}</span>
            <div className="h-[2px] flex-1 bg-gradient-to-l from-teal-200 to-transparent" />
        </div>
        {seccion.descripcion && <p className="text-sm text-gray-500 text-center mt-1 whitespace-pre-wrap">{seccion.descripcion}</p>}
    </div>
);

// ---- ResultadosDashboard ----
const ResultadosDashboard = ({ encuesta, onBack }) => {
    const [resultados, setResultados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('resumen');
    const [connected, setConnected] = useState(false);
    const [expandedRow, setExpandedRow] = useState(null);
    const [insights, setInsights] = useState(null);
    const [insightsLoading, setInsightsLoading] = useState(false);
    const [participacion, setParticipacion] = useState(null);
    const [participacionLoading, setParticipacionLoading] = useState(false);
    const [participacionSubTab, setParticipacionSubTab] = useState('pendientes');

    const fetchResultados = useCallback(async () => {
        try {
            const res = await axiosInstance.get(`/api/encuestas-producto/${encuesta.id}/resultados`);
            setResultados(res.data);
        } catch (err) {
            console.error('Error cargando resultados', err);
        } finally {
            setLoading(false);
        }
    }, [encuesta.id]);

    const fetchParticipacion = useCallback(async () => {
        setParticipacionLoading(true);
        try {
            const res = await axiosInstance.get(`/api/encuestas-producto/${encuesta.id}/participacion`);
            setParticipacion(res.data);
        } catch (err) {
            console.error('Error cargando participación', err);
        } finally {
            setParticipacionLoading(false);
        }
    }, [encuesta.id]);

    useEffect(() => {
        fetchResultados();
        const socketUrl = process.env.REACT_APP_API_URL || '';
        const socket = io(socketUrl, { transports: ['websocket', 'polling'] });
        socket.on('connect', () => setConnected(true));
        socket.on('disconnect', () => setConnected(false));
        socket.on('encuesta_resultado_actualizado', (data) => {
            if (data.encuesta_id === encuesta.id) {
                fetchResultados();
                setParticipacion(null);
            }
        });
        return () => socket.disconnect();
    }, [encuesta.id, fetchResultados]);

    useEffect(() => {
        if (tab === 'participacion' && !participacion && !participacionLoading) {
            fetchParticipacion();
        }
    }, [tab, participacion, participacionLoading, fetchParticipacion]);

    if (loading) return (
        <div className="flex items-center justify-center py-24">
            <div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full" />
        </div>
    );
    if (!resultados) return null;

    const { estadisticas, respuestas, totalRespuestas } = resultados;
    const preguntas = resultados.encuesta.preguntas || [];
    // Mismo orden que al responder: secciones por `orden` y preguntas por `orden`
    // dentro de cada sección.
    const gruposPreguntas = agruparPreguntasPorSeccion(preguntas, resultados.encuesta.secciones || []);
    const preguntasOrdenadas = gruposPreguntas.flatMap(g => g.preguntas);
    const pct = resultados.encuesta.totalInvitados > 0
        ? Math.round((totalRespuestas / resultados.encuesta.totalInvitados) * 100) : 0;
    const totalPendientes = Math.max(resultados.encuesta.totalInvitados - totalRespuestas, 0);

    const renderRespuestaValor = (val, pregunta) => {
        if (val && typeof val === 'object' && !Array.isArray(val) && 'sel' in val) {
            const sel = val.sel;
            const parts = [];
            if (Array.isArray(sel)) {
                parts.push(sel.filter(v => v !== OTRO_SENTINEL).join(', '));
            } else if (sel && sel !== OTRO_SENTINEL) {
                parts.push(sel);
            }
            if (val.otro) parts.push(`Otro: ${val.otro}`);
            return parts.filter(Boolean).join(' · ') || <span className="text-gray-400 italic">Sin respuesta</span>;
        }
        if (Array.isArray(val)) return val.join(', ');
        if (typeof val === 'object' && val !== null) {
            // Matriz: la llave es el criterio y el valor la posición de la columna elegida.
            // Las llaves del objeto llegan alfabetizadas, así que se recorre el orden
            // original de los criterios en vez del objeto.
            const esMatriz = ['matriz', 'matriz_multiple'].includes(pregunta?.tipo);
            const stats = estadisticas?.[pregunta?.id];
            const columnas = esMatriz ? columnasEnOrden(stats, pregunta) : [];
            const criterios = esMatriz
                ? criteriosEnOrden(stats, pregunta, val)
                : Object.keys(val);
            return criterios
                .map(criterio => {
                    const v = val[criterio];
                    const vacio = v === undefined || v === null || v === ''
                        || (Array.isArray(v) && v.length === 0);
                    if (vacio) return `${criterio}: Sin respuesta`;
                    const etiqueta = Array.isArray(v)
                        ? v.map(x => textoColumnaMatriz(x, columnas)).join(', ')
                        : textoColumnaMatriz(v, columnas);
                    return `${criterio}: ${etiqueta}`;
                })
                .join(' · ');
        }
        if (pregunta?.tipo === 'escala' && val !== null && val !== undefined && val !== '') {
            return textoColumnaMatriz(val, getPreguntaScaleOptions(pregunta));
        }
        return val || <span className="text-gray-400 italic">Sin respuesta</span>;
    };

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-4">
                    <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl transition-colors mt-0.5">
                        <ArrowLeft size={20} className="text-gray-600" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{resultados.encuesta.titulo}</h1>
                        <p className="text-gray-500 text-sm mt-0.5">{totalRespuestas} de {resultados.encuesta.totalInvitados} respuestas</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {connected
                        ? <span className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />En vivo</span>
                        : <span className="flex items-center gap-2 text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full"><span className="w-2 h-2 rounded-full bg-gray-400" />Reconectando</span>
                    }
                    <button onClick={fetchResultados} className="p-2 hover:bg-gray-100 rounded-xl transition-colors" title="Refrescar">
                        <RefreshCw size={16} className="text-gray-500" />
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Participación</span>
                    <span className="text-sm font-bold text-teal-600">{pct}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div className="h-full bg-teal-500 rounded-full" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6, ease: 'easeOut' }} />
                </div>
            </div>

            <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
                {[
                    { id: 'resumen', label: 'Resumen', icon: BarChart3 },
                    { id: 'respuestas', label: 'Respuestas', icon: List },
                    { id: 'participacion', label: 'Participación', icon: Users },
                    { id: 'invitados', label: 'Invitados externos', icon: Link2 },
                    { id: 'analizar', label: 'Analizar', icon: Sparkles },
                ].map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                        <t.icon size={16} />
                        {t.label}
                        {t.id === 'respuestas' && totalRespuestas > 0 && (
                            <span className="bg-teal-100 text-teal-700 text-xs px-1.5 py-0.5 rounded-full font-semibold">{totalRespuestas}</span>
                        )}
                        {t.id === 'participacion' && totalPendientes > 0 && (
                            <span className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full font-semibold">{totalPendientes}</span>
                        )}
                    </button>
                ))}
            </div>

            <AnimatePresence mode="wait">
                {tab === 'resumen' && (
                    <motion.div key="resumen" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
                        {gruposPreguntas.map((grupo, gi) => (
                        <React.Fragment key={grupo.seccion?.id || `grupo-${gi}`}>
                        {grupo.seccion && <SeccionHeader seccion={grupo.seccion} />}
                        {grupo.preguntas.map(pregunta => {
                            const stats = estadisticas?.[pregunta.id];
                            const escalaOpciones = getPreguntaScaleOptions(pregunta);
                            if (!stats) return null;
                            const columnasMatriz = columnasEnOrden(stats, pregunta);
                            return (
                                <div key={pregunta.id} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                                    <p className="font-medium text-gray-900 mb-5">{pregunta.texto}</p>
                                    {stats.tipo === 'opciones' && (
                                        <div className="space-y-3">
                                            {opcionesEnOrden(pregunta, stats.conteo).map(opcion => {
                                                const count = stats.conteo?.[opcion] || 0;
                                                const p = totalRespuestas > 0 ? (count / totalRespuestas * 100) : 0;
                                                return (
                                                    <div key={opcion}>
                                                        <div className="flex justify-between text-sm mb-1.5">
                                                            <span className="text-gray-700">{opcion}</span>
                                                            <span className="text-gray-400 tabular-nums">{count} · {p.toFixed(1)}%</span>
                                                        </div>
                                                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                                            <motion.div className="h-full bg-teal-500 rounded-full" initial={{ width: 0 }} animate={{ width: `${p}%` }} transition={{ duration: 0.5 }} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {stats.otros?.length > 0 && (
                                                <div className="mt-4 pt-4 border-t border-gray-100">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                                        Otros ({stats.otros.length})
                                                    </p>
                                                    <div className="space-y-2">
                                                        {stats.otros.map((o, idx) => (
                                                            <div key={idx} className="p-2.5 bg-amber-50 rounded-lg border border-amber-100">
                                                                <p className="text-sm text-gray-700">{o.texto}</p>
                                                                <p className="text-xs text-gray-400 mt-1">— {o.empleado}</p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {stats.tipo === 'escala' && (
                                        <div className="flex items-center gap-4">
                                            <span className="text-4xl font-bold text-teal-600 tabular-nums">{stats.promedio}</span>
                                            <div className="flex gap-1">{escalaOpciones.map((_, idx) => {
                                                const valor = idx + 1;
                                                return <Star key={valor} size={22} className={valor <= Math.round(stats.promedio) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'} />;
                                            })}</div>
                                            <span className="text-sm text-gray-400">{stats.total} respuestas</span>
                                        </div>
                                    )}
                                    {stats.tipo === 'matriz' && (
                                        <div className="space-y-3">
                                            {criteriosEnOrden(stats, pregunta, stats.promedios).map(criterio => {
                                                const promedio = stats.promedios?.[criterio] || 0;
                                                // El promedio es de posiciones de columna: sin la etiqueta se
                                                // malinterpreta cuando las columnas van de mejor a peor.
                                                const etiqueta = promedio > 0
                                                    ? textoColumnaMatriz(Math.round(promedio), columnasMatriz)
                                                    : null;
                                                return (
                                                    <div key={criterio} className="flex items-center gap-4">
                                                        <span className="w-44 text-sm text-gray-700 flex-shrink-0">{criterio}</span>
                                                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                                            <motion.div className="h-full bg-teal-500 rounded-full" initial={{ width: 0 }} animate={{ width: `${(promedio / Math.max(columnasMatriz.length || 1, 1)) * 100}%` }} transition={{ duration: 0.5 }} />
                                                        </div>
                                                        {etiqueta
                                                            ? <span className="text-sm font-medium text-teal-600 w-52 text-right truncate" title={etiqueta}>{etiqueta}</span>
                                                            : <span className="text-sm text-gray-400 w-52 text-right">Sin respuestas</span>
                                                        }
                                                        <span className="text-xs text-gray-400 w-16 text-right tabular-nums">pos. {promedio}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {stats.tipo === 'matriz_multiple' && (
                                        <div className="space-y-4">
                                            {criteriosEnOrden(stats, pregunta, stats.conteos).map(criterio => {
                                                const conteoColumnas = stats.conteos?.[criterio] || {};
                                                const cols = combinarOrden(
                                                    columnasMatriz.map(c => c.texto),
                                                    Object.keys(conteoColumnas)
                                                );
                                                return (
                                                    <div key={criterio} className="border border-gray-100 rounded-xl p-3 bg-gray-50/50">
                                                        <p className="text-sm font-semibold text-gray-800 mb-2">{criterio}</p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {cols.map(col => (
                                                                <div key={col} className="bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm flex items-center gap-2">
                                                                    <span className="text-xs text-gray-600 font-medium">{col}</span>
                                                                    <span className="text-xs font-bold text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded-md">{conteoColumnas[col] || 0}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {stats.tipo === 'texto' && (
                                        <div className="space-y-2 max-h-64 overflow-auto">
                                            {stats.respuestas.length === 0
                                                ? <p className="text-sm text-gray-400">Sin respuestas aún</p>
                                                : stats.respuestas.map((r, idx) => (
                                                    <div key={idx} className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                                                        <p className="text-sm text-gray-700">{r.texto}</p>
                                                        <p className="text-xs text-gray-400 mt-1.5">— {r.empleado}</p>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        </React.Fragment>
                        ))}
                    </motion.div>
                )}
                {tab === 'respuestas' && (
                    <motion.div key="respuestas" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                        {respuestas.length === 0 ? (
                            <div className="text-center py-16 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                                <Users size={40} className="text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500">Nadie ha respondido aún</p>
                            </div>
                        ) : (
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-100 overflow-hidden">
                                {respuestas.map(resp => (
                                    <div key={resp.id}>
                                        <button
                                            className="w-full flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors text-left"
                                            onClick={() => setExpandedRow(expandedRow === resp.id ? null : resp.id)}
                                        >
                                            <div className="w-9 h-9 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                                                {resp.empleadoNombre?.charAt(0) || '?'}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                                    {resp.empleadoNombre || 'Empleado'}
                                                    {resp.esInvitado && (
                                                        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700">Invitado</span>
                                                    )}
                                                </p>
                                                <div className="flex items-center gap-3 mt-0.5">
                                                    {resp.empleadoCorreo && <span className="text-xs text-gray-400">{resp.empleadoCorreo}</span>}
                                                    {resp.fechaRespuesta && <span className="text-xs text-gray-400">{new Date(resp.fechaRespuesta).toLocaleString('es-VE')}</span>}
                                                </div>
                                            </div>
                                            <ChevronRight size={16} className={`text-gray-400 transition-transform ${expandedRow === resp.id ? 'rotate-90' : ''}`} />
                                        </button>
                                        <AnimatePresence>
                                            {expandedRow === resp.id && (
                                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                    <div className="px-6 pb-5 pt-2 bg-gray-50 space-y-3">
                                                        {preguntasOrdenadas.map(pregunta => {
                                                            const val = resp.respuestas?.[String(pregunta.id)];
                                                            return (
                                                                <div key={pregunta.id}>
                                                                    <p className="text-xs font-medium text-gray-500 mb-1">{pregunta.texto}</p>
                                                                    <p className="text-sm text-gray-800">{renderRespuestaValor(val, pregunta)}</p>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}
                            </div>
                        )}
                    </motion.div>
                )}
                {tab === 'invitados' && (
                    <motion.div key="invitados" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                        <InvitacionesExternasPanel
                            encuesta={resultados.encuesta}
                            onCambio={() => { fetchResultados(); setParticipacion(null); }}
                        />
                    </motion.div>
                )}
                {tab === 'participacion' && (
                    <motion.div key="participacion" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
                        {participacionLoading && !participacion && (
                            <div className="flex items-center justify-center py-16">
                                <Loader2 size={28} className="animate-spin text-teal-500" />
                            </div>
                        )}
                        {participacion && (
                            <>
                                <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
                                    {[
                                        { id: 'pendientes', label: 'No han respondido', icon: UserX, count: participacion.totalPendientes },
                                        { id: 'respondieron', label: 'Respondieron', icon: UserCheck, count: participacion.totalRespondieron },
                                    ].map(st => (
                                        <button key={st.id} onClick={() => setParticipacionSubTab(st.id)}
                                            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${participacionSubTab === st.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                                            <st.icon size={15} />
                                            {st.label}
                                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${st.id === 'pendientes' ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'}`}>{st.count}</span>
                                        </button>
                                    ))}
                                </div>

                                {participacion.externos?.total > 0 && (
                                    <p className="text-sm text-gray-500">
                                        Además, {participacion.externos.total} {participacion.externos.total === 1 ? 'link externo' : 'links externos'}:{' '}
                                        {participacion.externos.respondieron} {participacion.externos.respondieron === 1 ? 'respondido' : 'respondidos'} y{' '}
                                        {participacion.externos.pendientes} sin responder.{' '}
                                        <button onClick={() => setTab('invitados')} className="text-teal-700 font-medium hover:underline">Ver invitados externos</button>
                                    </p>
                                )}

                                {(participacionSubTab === 'pendientes' ? participacion.pendientes : participacion.respondieron).length === 0 ? (
                                    <div className="text-center py-16 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                                        {participacionSubTab === 'pendientes'
                                            ? <><CheckCircle size={40} className="text-emerald-300 mx-auto mb-3" /><p className="text-gray-500">Todos los invitados ya respondieron</p></>
                                            : <><Users size={40} className="text-gray-300 mx-auto mb-3" /><p className="text-gray-500">Nadie ha respondido aún</p></>
                                        }
                                    </div>
                                ) : (
                                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-100 overflow-hidden">
                                        {(participacionSubTab === 'pendientes' ? participacion.pendientes : participacion.respondieron).map(emp => (
                                            <div key={emp.id} className="flex items-center gap-4 px-6 py-4">
                                                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${participacionSubTab === 'pendientes' ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'}`}>
                                                    {emp.nombre?.charAt(0) || '?'}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900">{emp.nombre}</p>
                                                    <div className="flex items-center gap-3 mt-0.5">
                                                        {emp.cargo && <span className="text-xs text-gray-400">{emp.cargo}</span>}
                                                        {emp.correo && <span className="text-xs text-gray-400">{emp.correo}</span>}
                                                    </div>
                                                </div>
                                                {participacionSubTab === 'pendientes' && emp.extension && (
                                                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-teal-700 bg-teal-50 px-2.5 py-1 rounded-lg flex-shrink-0 tabular-nums" title="Extensión telefónica">
                                                        <Phone size={12} />
                                                        Ext. {emp.extension}
                                                    </span>
                                                )}
                                                {emp.fechaRespuesta && (
                                                    <span className="text-xs text-gray-400 flex-shrink-0">{new Date(emp.fechaRespuesta).toLocaleString('es-VE')}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </motion.div>
                )}
                {tab === 'analizar' && (
                    <motion.div key="analizar" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
                        {!insights && !insightsLoading && (
                            <div className="text-center py-16 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                                <Sparkles size={40} className="text-violet-300 mx-auto mb-3" />
                                <h3 className="text-lg font-semibold text-gray-700 mb-1">Análisis con IA</h3>
                                <p className="text-gray-400 text-sm mb-4">Genera insights accionables a partir de {totalRespuestas} respuestas</p>
                                <button
                                    onClick={async () => {
                                        setInsightsLoading(true);
                                        try {
                                            const res = await axiosInstance.get(`/api/encuestas-producto/${encuesta.id}/ai/insights`);
                                            setInsights(res.data);
                                        } catch (err) {
                                            setInsights({ error: err.response?.data?.error || 'Error al generar insights' });
                                        } finally { setInsightsLoading(false); }
                                    }}
                                    disabled={totalRespuestas < 2}
                                    className="px-6 py-2.5 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition-colors text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
                                >
                                    <Sparkles size={16} /> Generar análisis
                                </button>
                            </div>
                        )}
                        {insightsLoading && (
                            <div className="text-center py-16">
                                <Loader2 size={32} className="animate-spin text-violet-500 mx-auto mb-3" />
                                <p className="text-gray-500">Analizando respuestas...</p>
                            </div>
                        )}
                        {insights && !insights.error && (
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                                <div className="flex items-center justify-between mb-5">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                                            <Sparkles size={16} className="text-violet-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900">Análisis inteligente</h3>
                                            <p className="text-xs text-gray-400">Generado con IA · {totalRespuestas} respuestas</p>
                                        </div>
                                    </div>
                                    {insights.sentimiento_general && (
                                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                                            insights.sentimiento_general === 'positivo' ? 'bg-green-100 text-green-700' :
                                            insights.sentimiento_general === 'negativo' ? 'bg-red-100 text-red-700' :
                                            'bg-amber-100 text-amber-700'
                                        }`}>{insights.sentimiento_general}</span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-700 mb-4">{insights.resumen}</p>
                                {insights.insights?.length > 0 && (
                                    <div className="space-y-2 mb-4">
                                        {insights.insights.map((ins, i) => (
                                            <div key={i} className={`p-3 rounded-xl border ${
                                                ins.tipo === 'positivo' ? 'bg-green-50 border-green-200' :
                                                ins.tipo === 'negativo' ? 'bg-red-50 border-red-200' :
                                                'bg-gray-50 border-gray-200'
                                            }`}>
                                                <p className="text-sm font-medium text-gray-900">{ins.titulo}</p>
                                                <p className="text-xs text-gray-600 mt-0.5">{ins.detalle}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {insights.recomendaciones?.length > 0 && (
                                    <div>
                                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Recomendaciones</h4>
                                        <ul className="space-y-1">
                                            {insights.recomendaciones.map((r, i) => (
                                                <li key={i} className="text-sm text-gray-600 flex gap-2">
                                                    <span className="text-amber-500 mt-0.5">-</span>{r}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                        {insights?.error && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600">{insights.error}</div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

// ---- Historial de un empleado (buscar cualquier usuario y ver sus encuestas) ----
const ESTADO_HISTORIAL = {
    respondida: { label: 'Respondida', icon: UserCheck, badge: 'bg-teal-100 text-teal-700' },
    pendiente: { label: 'Pendiente', icon: Clock, badge: 'bg-amber-100 text-amber-700' },
    vencida: { label: 'Vencida sin responder', icon: AlertCircle, badge: 'bg-red-100 text-red-700' },
};

// ---- Estadísticas de usuarios (quién responde más y quién responde a tiempo) ----
const COLORES_PODIO = [
    { medalla: 'bg-amber-100 text-amber-700 border-amber-200', barra: 'from-amber-200 to-amber-50' },
    { medalla: 'bg-gray-100 text-gray-600 border-gray-200', barra: 'from-gray-200 to-gray-50' },
    { medalla: 'bg-orange-100 text-orange-700 border-orange-200', barra: 'from-orange-200 to-orange-50' },
];

const COLUMNAS_ESTADISTICAS = [
    { id: 'respondidasATiempo', label: 'A tiempo', ancho: 'w-24' },
    { id: 'tasaRespuesta', label: 'Tasa de respuesta', ancho: 'w-56' },
    { id: 'respondidasTarde', label: 'Tarde', ancho: 'w-20' },
    { id: 'vencidasSinResponder', label: 'Vencidas', ancho: 'w-24' },
];

const EstadisticasUsuariosPanel = ({ onVolver }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [orden, setOrden] = useState({ campo: 'respondidasATiempo', dir: 'desc' });

    useEffect(() => {
        let activo = true;
        axiosInstance.get('/api/encuestas-producto/estadisticas-usuarios')
            .then(res => { if (activo) setData(res.data); })
            .catch(err => {
                console.error('Error cargando estadísticas de usuarios', err);
                if (activo) setError(err.response?.data?.error || 'No se pudieron cargar las estadísticas de usuarios');
            })
            .finally(() => { if (activo) setLoading(false); });
        return () => { activo = false; };
    }, []);

    const cambiarOrden = (campo) => setOrden(prev => (
        prev.campo === campo
            ? { campo, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
            : { campo, dir: 'desc' }
    ));

    const usuarios = data?.usuarios || [];
    const ordenados = [...usuarios].sort((a, b) => {
        const factor = orden.dir === 'desc' ? -1 : 1;
        const va = a[orden.campo] ?? 0;
        const vb = b[orden.campo] ?? 0;
        if (va === vb) return `${a.nombre || ''}`.localeCompare(`${b.nombre || ''}`);
        return va < vb ? factor : -factor;
    });
    const podio = usuarios.slice(0, 3);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Estadísticas de usuarios</h2>
                    <p className="text-gray-500 text-sm mt-0.5">
                        Quiénes responden más encuestas y, sobre todo, quiénes responden antes de que se les venza
                        {data?.totalEmpleados ? ` · ${data.totalEmpleados} empleados` : ''}
                    </p>
                </div>
                <button onClick={onVolver}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                    <ArrowLeft size={16} /> Volver al historial
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 size={28} className="animate-spin text-teal-500" />
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600">{error}</div>
            ) : usuarios.length === 0 ? (
                <div className="text-center py-16 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                    <Trophy size={40} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Todavía no hay participación registrada</p>
                </div>
            ) : (
                <>
                    {podio.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {podio.map((u, i) => (
                                <motion.div key={u.id}
                                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                                    className={`rounded-2xl border border-gray-100 shadow-sm p-5 bg-gradient-to-b ${COLORES_PODIO[i].barra}`}>
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className={`w-8 h-8 rounded-full border flex items-center justify-center text-sm font-bold ${COLORES_PODIO[i].medalla}`}>
                                            {i + 1}
                                        </span>
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-gray-900 truncate">{u.nombre}</p>
                                            {u.cargo && <p className="text-xs text-gray-500 truncate">{u.cargo}</p>}
                                        </div>
                                    </div>
                                    <div className="bg-white/80 rounded-xl px-3 py-2.5">
                                        <p className="text-2xl font-bold text-teal-600 tabular-nums leading-none">{u.respondidasATiempo}</p>
                                        <p className="text-xs text-gray-500 mt-1">respondidas a tiempo</p>
                                        <p className="text-xs text-gray-400 mt-1.5">
                                            {u.totalRespondidas} de {u.totalInvitadas} encuestas · {Math.round(u.tasaATiempo || 0)}% a tiempo
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}

                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-100 bg-gray-50/70">
                                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Empleado</th>
                                        {COLUMNAS_ESTADISTICAS.map(col => (
                                            <th key={col.id} className={`px-4 py-3 text-left ${col.ancho}`}>
                                                <button onClick={() => cambiarOrden(col.id)}
                                                    className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-800 transition-colors">
                                                    {col.label}
                                                    {orden.campo === col.id && (
                                                        orden.dir === 'desc' ? <ChevronDown size={13} /> : <ChevronUp size={13} />
                                                    )}
                                                </button>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {ordenados.map(u => {
                                        const tasa = Math.max(0, Math.min(100, Math.round(u.tasaRespuesta || 0)));
                                        return (
                                            <tr key={u.id} className="hover:bg-gray-50/60 transition-colors">
                                                <td className="px-5 py-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                                            {u.nombre?.charAt(0) || '?'}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="font-medium text-gray-900 truncate">{u.nombre}</p>
                                                            {(u.cargo || u.correo) && (
                                                                <p className="text-xs text-gray-400 truncate">{[u.cargo, u.correo].filter(Boolean).join(' · ')}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${ESTADO_HISTORIAL.respondida.badge}`}>
                                                        <CheckCircle size={13} />{u.respondidasATiempo}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden min-w-24">
                                                            <motion.div className="h-full bg-teal-500 rounded-full"
                                                                initial={{ width: 0 }} animate={{ width: `${tasa}%` }} transition={{ duration: 0.5 }} />
                                                        </div>
                                                        <span className="text-xs text-gray-500 tabular-nums w-24 text-right">
                                                            {tasa}% · {u.totalRespondidas}/{u.totalInvitadas}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {u.respondidasTarde > 0
                                                        ? <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${ESTADO_HISTORIAL.pendiente.badge}`}>
                                                            <Clock size={13} />{u.respondidasTarde}
                                                        </span>
                                                        : <span className="text-xs text-gray-300">—</span>
                                                    }
                                                </td>
                                                <td className="px-4 py-3">
                                                    {u.vencidasSinResponder > 0
                                                        ? <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${ESTADO_HISTORIAL.vencida.badge}`}>
                                                            <AlertCircle size={13} />{u.vencidasSinResponder}
                                                        </span>
                                                        : <span className="text-xs text-gray-300">—</span>
                                                    }
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const HistorialEmpleadoView = ({ onBack }) => {
    const [empleados, setEmpleados] = useState([]);
    const [loadingEmpleados, setLoadingEmpleados] = useState(true);
    const [search, setSearch] = useState('');
    const [empleadoSel, setEmpleadoSel] = useState(null);
    const [historial, setHistorial] = useState(null);
    const [loadingHistorial, setLoadingHistorial] = useState(false);
    const [verEstadisticas, setVerEstadisticas] = useState(false);

    useEffect(() => {
        axiosInstance.get('/api/empleados')
            .then(r => setEmpleados(r.data))
            .catch(console.error)
            .finally(() => setLoadingEmpleados(false));
    }, []);

    const filtered = search.trim().length < 2 ? [] : empleados
        .filter(e =>
            e.nombre?.toLowerCase().includes(search.toLowerCase()) ||
            e.cargo?.toLowerCase().includes(search.toLowerCase()) ||
            e.departamento?.toLowerCase().includes(search.toLowerCase())
        )
        .slice(0, 20);

    const seleccionarEmpleado = async (emp) => {
        setEmpleadoSel(emp);
        setSearch('');
        setHistorial(null);
        setLoadingHistorial(true);
        try {
            const res = await axiosInstance.get(`/api/encuestas-producto/empleados/${emp.id}/historial`);
            setHistorial(res.data);
        } catch (err) {
            console.error('Error cargando historial', err);
            setHistorial({ error: 'No se pudo cargar el historial' });
        } finally {
            setLoadingHistorial(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                    <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                        <ArrowLeft size={20} className="text-gray-600" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Historial por usuario</h1>
                        <p className="text-gray-500 text-sm mt-0.5">Busca a un empleado y revisa qué encuestas respondió, tiene pendientes o dejó vencidas</p>
                    </div>
                </div>
                {!verEstadisticas && (
                    <button onClick={() => setVerEstadisticas(true)}
                        className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors text-sm font-medium">
                        <Trophy size={16} /> Estadísticas de usuarios
                    </button>
                )}
            </div>

            {verEstadisticas ? (
                <EstadisticasUsuariosPanel onVolver={() => setVerEstadisticas(false)} />
            ) : (
            <>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-3">
                <div className="relative">
                    <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text" value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Buscar por nombre, cargo o departamento..."
                        className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                </div>
                {loadingEmpleados ? (
                    <div className="text-sm text-gray-400 px-1">Cargando empleados...</div>
                ) : filtered.length > 0 && (
                    <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
                        {filtered.map(emp => (
                            <button key={emp.id} onClick={() => seleccionarEmpleado(emp)}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors">
                                <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                                    {emp.nombre?.charAt(0) || '?'}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-800 truncate">{emp.nombre}</p>
                                    {(emp.cargo || emp.departamento) && (
                                        <p className="text-xs text-gray-400 truncate">{[emp.cargo, emp.departamento].filter(Boolean).join(' · ')}</p>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
                {search.trim().length >= 2 && !loadingEmpleados && filtered.length === 0 && (
                    <p className="text-sm text-gray-400 px-1">Sin resultados</p>
                )}
            </div>

            {empleadoSel && (
                <div className="space-y-4">
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
                        <div className="w-11 h-11 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-base font-bold flex-shrink-0">
                            {empleadoSel.nombre?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 truncate">{empleadoSel.nombre}</p>
                            {empleadoSel.correo && <p className="text-xs text-gray-400 truncate">{empleadoSel.correo}</p>}
                            {historial?.empleado?.extension && (
                                <p className="inline-flex items-center gap-1.5 text-xs font-medium text-teal-700 mt-1 tabular-nums" title="Extensión telefónica">
                                    <Phone size={12} />
                                    Ext. {historial.empleado.extension}
                                </p>
                            )}
                        </div>
                        {historial && !historial.error && (
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-teal-100 text-teal-700">{historial.totalRespondidas} respondidas</span>
                                <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-amber-100 text-amber-700">{historial.totalPendientes} pendientes</span>
                                <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-red-100 text-red-700">{historial.totalVencidas} vencidas</span>
                            </div>
                        )}
                    </div>

                    {loadingHistorial ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 size={28} className="animate-spin text-teal-500" />
                        </div>
                    ) : historial?.error ? (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600">{historial.error}</div>
                    ) : historial?.encuestas.length === 0 ? (
                        <div className="text-center py-16 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                            <History size={40} className="text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">Este empleado no ha sido invitado a ninguna encuesta</p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-100 overflow-hidden">
                            {historial.encuestas.map(enc => {
                                const cfg = ESTADO_HISTORIAL[enc.estado];
                                return (
                                    <div key={enc.id} className="flex items-center gap-4 px-6 py-4">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">{enc.titulo}</p>
                                            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                                                {enc.fechaLimite && <span>Límite: {new Date(enc.fechaLimite).toLocaleDateString('es-VE')}</span>}
                                                {enc.fechaRespuesta && <span>Respondió: {new Date(enc.fechaRespuesta).toLocaleDateString('es-VE')}</span>}
                                                {!enc.activa && <span className="text-gray-400">Inactiva</span>}
                                            </div>
                                        </div>
                                        <span className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ${cfg.badge}`}>
                                            <cfg.icon size={13} />{cfg.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
            </>
            )}
        </div>
    );
};

// ---- Impresión en papel ----
// Genera una versión imprimible de la encuesta (preguntas en blanco) para
// entregarla a quienes deseen responderla en físico.
const escapeHtml = (str) => String(str ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const resolveImgUrl = (url) => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

const renderPreguntaImpresion = (pregunta, numero) => {
    const opciones = (Array.isArray(pregunta.opciones) ? pregunta.opciones : []).map(normalizeOpcion);
    const permiteOtro = pregunta.permiteOtro && ['opcion_unica', 'opcion_multiple'].includes(pregunta.tipo);
    const imgOpcion = (op) => op.imagen_url
        ? `<img class="opt-img" src="${resolveImgUrl(op.imagen_url)}" alt="" />`
        : '';

    let cuerpo = '';
    switch (pregunta.tipo) {
        case 'texto_corto':
            cuerpo = '<div class="write-line"></div>';
            break;
        case 'texto_largo':
            cuerpo = '<div class="write-line"></div><div class="write-line"></div><div class="write-line"></div><div class="write-line"></div>';
            break;
        case 'opcion_unica':
        case 'opcion_multiple': {
            const marca = pregunta.tipo === 'opcion_unica' ? 'circle' : 'square';
            const items = opciones.map(op =>
                `<div class="opt"><span class="mark ${marca}"></span>${imgOpcion(op)}<span class="opt-txt">${escapeHtml(op.texto)}</span></div>`
            ).join('');
            const otro = permiteOtro
                ? `<div class="opt"><span class="mark ${marca}"></span><span class="opt-txt">Otro:</span><span class="inline-line"></span></div>`
                : '';
            cuerpo = `<div class="opts">${items}${otro}</div>`;
            break;
        }
        case 'escala': {
            const escala = getPreguntaScaleOptions(pregunta);
            cuerpo = `<div class="scale">${escala.map(op =>
                `<div class="scale-item"><span class="mark circle"></span><span class="scale-lbl">${escapeHtml(op.texto)}</span></div>`
            ).join('')}</div>`;
            break;
        }
        case 'matriz':
        case 'matriz_multiple': {
            const marca = pregunta.tipo === 'matriz' ? 'circle' : 'square';
            const config = getPreguntaMatrizConfig(pregunta);
            const thead = `<tr><th></th>${config.columnas.map(c => `<th>${escapeHtml(c.texto)}</th>`).join('')}</tr>`;
            const tbody = config.criterios.map(cr =>
                `<tr><td class="crit">${escapeHtml(cr.texto)}</td>${config.columnas.map(() =>
                    `<td class="cell"><span class="mark ${marca}"></span></td>`
                ).join('')}</tr>`
            ).join('');
            cuerpo = `<table class="matriz"><thead>${thead}</thead><tbody>${tbody}</tbody></table>`;
            break;
        }
        default:
            cuerpo = '<div class="write-line"></div>';
    }

    const imgPregunta = pregunta.imagenUrl
        ? `<img class="preg-img" src="${resolveImgUrl(pregunta.imagenUrl)}" alt="" />`
        : '';

    return `<div class="pregunta">
        <div class="preg-titulo"><span class="num">${numero}.</span><span>${escapeHtml(pregunta.texto)}${pregunta.obligatoria ? ' <span class="req">*</span>' : ''}</span></div>
        ${imgPregunta}
        ${cuerpo}
    </div>`;
};

const generarHTMLImpresion = (encuesta) => {
    const preguntas = (encuesta.preguntas || []).filter(p => !p.archivada);
    const secciones = [...(encuesta.secciones || [])].sort((a, b) => (a.orden || 0) - (b.orden || 0));

    // Agrupar: primero preguntas sin sección, luego cada sección en orden.
    const grupos = [
        { seccion: null, preguntas: preguntas.filter(p => !p.seccionId) },
        ...secciones.map(s => ({
            seccion: s,
            preguntas: preguntas.filter(p => String(p.seccionId) === String(s.id)),
        })),
    ].filter(g => g.preguntas.length > 0);

    let numero = 0;
    const bloques = grupos.map(g => {
        const cabeceraSeccion = g.seccion
            ? `<div class="seccion"><h2>${escapeHtml(g.seccion.titulo)}</h2>${g.seccion.descripcion ? `<p>${escapeHtml(g.seccion.descripcion)}</p>` : ''}</div>`
            : '';
        const preguntasHtml = g.preguntas.map(p => {
            numero += 1;
            return renderPreguntaImpresion(p, numero);
        }).join('');
        return cabeceraSeccion + preguntasHtml;
    }).join('');

    const bannerHtml = encuesta.bannerUrl
        ? `<img class="banner" src="${resolveImgUrl(encuesta.bannerUrl)}" alt="" />` : '';
    const extraHtml = encuesta.imagenExtraUrl
        ? `<img class="extra" src="${resolveImgUrl(encuesta.imagenExtraUrl)}" alt="" />` : '';

    return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(encuesta.titulo)}</title>
<style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; margin: 0; padding: 32px 40px; font-size: 13px; line-height: 1.5; }
    .banner { width: 100%; max-height: 160px; object-fit: cover; border-radius: 8px; margin-bottom: 16px; }
    .extra { width: 100%; max-height: 260px; object-fit: contain; margin: 12px 0; }
    h1 { font-size: 22px; margin: 0 0 4px; }
    .desc { color: #444; margin: 0 0 16px; white-space: pre-wrap; }
    .datos { display: flex; gap: 32px; margin: 14px 0 22px; padding-bottom: 16px; border-bottom: 2px solid #0d9488; }
    .datos .campo { flex: 1; display: flex; align-items: flex-end; gap: 6px; font-size: 12px; color: #555; }
    .datos .campo .linea { flex: 1; border-bottom: 1px solid #999; height: 18px; }
    .seccion { margin: 22px 0 10px; border-top: 1px solid #ddd; padding-top: 14px; }
    .seccion h2 { font-size: 14px; text-transform: uppercase; letter-spacing: .5px; color: #0d9488; margin: 0 0 2px; }
    .seccion p { margin: 0; color: #555; font-size: 12px; }
    .pregunta { margin: 0 0 16px; page-break-inside: avoid; }
    .preg-titulo { display: flex; gap: 6px; font-weight: bold; margin-bottom: 8px; }
    .preg-titulo .num { color: #0d9488; }
    .req { color: #dc2626; }
    .preg-img { max-width: 260px; max-height: 180px; object-fit: contain; display: block; margin: 4px 0 10px; border: 1px solid #eee; border-radius: 6px; }
    .opts { display: flex; flex-direction: column; gap: 7px; }
    .opt { display: flex; align-items: center; gap: 9px; }
    .opt-txt { }
    .opt-img { width: 34px; height: 34px; object-fit: cover; border-radius: 4px; border: 1px solid #ddd; }
    .mark { display: inline-block; width: 15px; height: 15px; border: 1.5px solid #333; flex-shrink: 0; }
    .mark.circle { border-radius: 50%; }
    .mark.square { border-radius: 3px; }
    .inline-line { flex: 1; border-bottom: 1px solid #999; height: 15px; margin-left: 6px; }
    .write-line { border-bottom: 1px solid #bbb; height: 22px; }
    .scale { display: flex; gap: 22px; flex-wrap: wrap; padding: 4px 0; }
    .scale-item { display: flex; flex-direction: column; align-items: center; gap: 6px; }
    .scale-lbl { font-size: 11px; text-align: center; max-width: 90px; }
    table.matriz { border-collapse: collapse; width: 100%; }
    table.matriz th, table.matriz td { border: 1px solid #ccc; padding: 7px 8px; font-size: 11px; text-align: center; }
    table.matriz th { background: #f1f5f5; font-weight: bold; }
    table.matriz td.crit { text-align: left; font-weight: normal; }
    table.matriz td.cell { text-align: center; }
    table.matriz td.cell .mark { margin: 0 auto; }
    .pie { margin-top: 26px; padding-top: 12px; border-top: 1px solid #ddd; font-size: 10px; color: #999; text-align: center; }
    @media print {
        body { padding: 0; }
        .pregunta { page-break-inside: avoid; }
    }
</style>
</head>
<body>
    ${bannerHtml}
    <h1>${escapeHtml(encuesta.titulo)}</h1>
    ${encuesta.descripcion ? `<p class="desc">${escapeHtml(encuesta.descripcion)}</p>` : ''}
    <div class="datos">
        <div class="campo">Nombre:<span class="linea"></span></div>
        <div class="campo">Fecha:<span class="linea"></span></div>
    </div>
    ${extraHtml}
    ${bloques}
    <div class="pie">Encuesta impresa desde Centro Sura — para llenar en físico.</div>
    <script>
        window.onload = function () { window.focus(); window.print(); };
    </script>
</body>
</html>`;
};

// ---- Estado de cada encuesta para el usuario que la mira ----
// El backend manda `estadoUsuario`; si no llega (respuesta vieja en caché) se
// deduce con los datos que la tarjeta ya tiene.
const estadoEncuesta = (encuesta) => {
    if (encuesta?.estadoUsuario) return encuesta.estadoUsuario;
    const vencida = encuesta?.fechaLimite && new Date(encuesta.fechaLimite) <= new Date();
    if (vencida || !encuesta?.activa) return 'expirada';
    return encuesta?.yaRespondio ? 'respondida' : 'pendiente';
};

// Primero lo que sigue abierto y falta por responder, de último lo vencido.
const ORDEN_ESTADOS = { pendiente: 0, respondida: 1, expirada: 2 };

const FILTROS_ESTADO = [
    { id: 'todas', nombre: 'Todas' },
    { id: 'pendiente', nombre: 'Pendientes' },
    { id: 'respondida', nombre: 'Respondidas' },
    { id: 'expirada', nombre: 'Expiradas' },
];

// Nombre con el que quien gestiona reconoce la encuesta: el interno cuando existe.
// Quien la responde ve SIEMPRE el nombre público.
const nombreGestion = (encuesta) => `${encuesta?.tituloPrivado || ''}`.trim() || encuesta?.titulo || '';

const ChipEstado = ({ estado, diasRestantes, invitado = true }) => {
    // A quien no está invitado no le corresponde responder: la encuesta solo está abierta.
    if (estado === 'pendiente' && !invitado) {
        return (
            <span className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full border bg-gray-50 text-gray-500 border-gray-200 whitespace-nowrap">
                <Clock size={12} />Abierta
            </span>
        );
    }
    const urgente = estado === 'pendiente' && typeof diasRestantes === 'number' && diasRestantes <= 5;
    const config = {
        pendiente: {
            Icono: Clock,
            texto: urgente
                ? (diasRestantes <= 0 ? 'Último día' : `Quedan ${diasRestantes} ${diasRestantes === 1 ? 'día' : 'días'}`)
                : 'Pendiente',
            clases: urgente
                ? 'bg-amber-100 text-amber-800 border-amber-300'
                : 'bg-amber-50 text-amber-700 border-amber-200',
        },
        respondida: { Icono: CheckCircle, texto: 'Respondida', clases: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
        expirada: { Icono: AlertCircle, texto: 'Expirada', clases: 'bg-gray-100 text-gray-500 border-gray-200' },
    }[estado];
    if (!config) return null;
    const { Icono } = config;
    return (
        <span className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full border whitespace-nowrap ${config.clases}`}>
            <Icono size={12} />{config.texto}
        </span>
    );
};

// ---- Main View ----
const EMPTY_FORM = {
    id: null,
    titulo: '', tituloPrivado: '', descripcion: '', bannerUrl: null, imagenExtraUrl: null,
    activa: true, fechaLimite: '', preguntas: [], secciones: [], empleadosInvitados: [],
    totalRespuestas: 0,
};

const EncuestasProductoView = () => {
    const { user } = useAuth();
    // Solo Mercadeo administra (crea/edita/borra/ve resultados); admin/ti quedan fuera.
    const canManage = user?.role === 'mercadeo';

    const [encuestas, setEncuestas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list'); // 'list' | 'respond' | 'results' | 'form' | 'historial'
    const [selectedEncuesta, setSelectedEncuesta] = useState(null);
    const [respuestas, setRespuestas] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'
    const [formData, setFormData] = useState(EMPTY_FORM);
    // Navegación sección por sección en la vista de respuesta
    const [seccionActualIdx, setSeccionActualIdx] = useState(0);
    const [historialNav, setHistorialNav] = useState([]); // índices visitados (para "Anterior")
    // Listado: buscador + filtro por estado
    const [busqueda, setBusqueda] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('todas');

    const encuestasConEstado = useMemo(
        () => encuestas.map(e => ({ ...e, estado: estadoEncuesta(e) })),
        [encuestas]
    );

    const conteosEstado = useMemo(() => {
        const conteos = { todas: encuestasConEstado.length, pendiente: 0, respondida: 0, expirada: 0 };
        encuestasConEstado.forEach(e => { conteos[e.estado] = (conteos[e.estado] || 0) + 1; });
        return conteos;
    }, [encuestasConEstado]);

    // Lista final del listado: filtro por estado + búsqueda, y el orden por defecto
    // (pendientes primero, luego respondidas, de último las expiradas).
    const encuestasVisibles = useMemo(() => {
        const termino = sinAcentos(busqueda);

        const filtradas = encuestasConEstado.filter(e => {
            if (filtroEstado !== 'todas' && e.estado !== filtroEstado) return false;
            if (!termino) return true;
            // El nombre interno solo existe (y solo se busca) para quien gestiona.
            const campos = [e.titulo, e.descripcion];
            if (canManage) campos.push(e.tituloPrivado);
            return campos.some(c => c && sinAcentos(c).includes(termino));
        });

        // Sin fecha límite = al final del grupo.
        const limite = (e) => {
            if (!e.fechaLimite) return Infinity;
            const t = new Date(e.fechaLimite).getTime();
            return Number.isNaN(t) ? Infinity : t;
        };
        const creacion = (e) => {
            const t = e.fechaCreacion ? new Date(e.fechaCreacion).getTime() : NaN;
            return Number.isNaN(t) ? 0 : t;
        };

        return filtradas.sort((a, b) => {
            const porEstado = (ORDEN_ESTADOS[a.estado] ?? 99) - (ORDEN_ESTADOS[b.estado] ?? 99);
            if (porEstado !== 0) return porEstado;
            const la = limite(a), lb = limite(b);
            if (la !== lb) return la < lb ? -1 : 1;
            return creacion(b) - creacion(a);
        });
    }, [encuestasConEstado, filtroEstado, busqueda, canManage]);

    const calcularVisibilidadEncuesta = calcularVisibilidadEncuestaPura;

    // Autoguardado: Cargar borrador inicial
    useEffect(() => {
        if (view === 'respond' && selectedEncuesta) {
            const draft = localStorage.getItem(`encuesta_draft_${selectedEncuesta.id}_${user?.id}`);
            if (draft) {
                try {
                    setRespuestas(JSON.parse(draft));
                } catch (e) {
                    console.error('Error parseando borrador de encuesta', e);
                }
            }
            // Si no hay draft, se mantiene inicializado vacío por handleOpenEncuesta
        }
    }, [view, selectedEncuesta, user?.id]);

    // Autoguardado: Guardar en localStorage cada vez que cambien las respuestas
    useEffect(() => {
        if (view === 'respond' && selectedEncuesta) {
            if (Object.keys(respuestas).length > 0) {
                localStorage.setItem(`encuesta_draft_${selectedEncuesta.id}_${user?.id}`, JSON.stringify(respuestas));
            }
        }
    }, [respuestas, view, selectedEncuesta, user?.id]);

    useEffect(() => { fetchEncuestas(); }, []);

    // Link del correo de invitación: /encuestas-producto?encuesta=<id> abre esa
    // encuesta directo si todavía se puede responder. Se usa una sola vez y se
    // limpia de la URL para que un recargue no la vuelva a abrir.
    const deepLinkUsado = useRef(false);
    useEffect(() => {
        if (deepLinkUsado.current || loading || encuestas.length === 0) return;
        const id = new URLSearchParams(window.location.search).get('encuesta');
        if (!id) return;
        deepLinkUsado.current = true;
        window.history.replaceState(null, '', window.location.pathname);
        const encuesta = encuestas.find((e) => String(e.id) === id);
        const vigente = encuesta && encuesta.activa && encuesta.estaInvitado
            && (!encuesta.fechaLimite || new Date(encuesta.fechaLimite) > new Date());
        if (vigente) handleOpenEncuesta(encuesta);
    }, [encuestas, loading]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchEncuestas = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/encuestas-producto');
            setEncuestas(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenEncuesta = async (encuesta) => {
        try {
            const res = await axiosInstance.get(`/api/encuestas-producto/${encuesta.id}`);
            setSelectedEncuesta(res.data);
            
            if (encuesta.yaRespondio) {
                const resResp = await axiosInstance.get(`/api/encuestas-producto/${encuesta.id}/mi-respuesta`);
                setRespuestas(resResp.data.respuestas || {});
            } else {
                setRespuestas({});
                // localstorage se encarga del borrador dentro de useEffect
            }

            setError('');
            setSeccionActualIdx(0);
            setHistorialNav([]);
            setView('respond');
        } catch (err) { console.error(err); }
    };

    const handleSubmitRespuesta = async () => {
        setError('');
        const { preguntasVisibles } = calcularVisibilidadEncuesta(selectedEncuesta, respuestas);
        
        const preguntas = (selectedEncuesta.preguntas || []).filter(p => {
            if (p.archivada) return false;
            return preguntasVisibles.has(p.id);
        });
        const errorObligatoria = validarObligatorias(preguntas, respuestas);
        if (errorObligatoria) {
            setError(errorObligatoria);
            return;
        }

        // Convertir índices de opciones con imágenes a texto para el backend
        const finalRespuestas = convertirRespuestasParaEnvio(preguntas, respuestas);

        setSubmitting(true);
        try {
            await axiosInstance.post(`/api/encuestas-producto/${selectedEncuesta.id}/responder`, { 
                respuestas: finalRespuestas,
                preguntas_visibles: Array.from(preguntasVisibles)
            });
            localStorage.removeItem(`encuesta_draft_${selectedEncuesta.id}_${user?.id}`);
            setView('success');
            fetchEncuestas();
        } catch (err) {
            setError(err.response?.data?.error || 'Error al enviar respuesta');
        } finally {
            setSubmitting(false);
        }
    };

    const openCreateForm = () => {
        setFormMode('create');
        setFormData(EMPTY_FORM);
        setView('form');
    };

    const openEditForm = async (encuesta) => {
        try {
            const res = await axiosInstance.get(`/api/encuestas-producto/${encuesta.id}`);
            const e = res.data;
            setFormMode('edit');
            setFormData({
                id: e.id,
                titulo: e.titulo || '',
                tituloPrivado: e.tituloPrivado || '',
                descripcion: e.descripcion || '',
                bannerUrl: e.bannerUrl || null,
                imagenExtraUrl: e.imagenExtraUrl || null,
                activa: !!e.activa,
                fechaLimite: e.fechaLimite ? e.fechaLimite.slice(0, 16) : '',
                secciones: (e.secciones || []).map(s => ({ ...s, clientKey: String(s.id) })),
                preguntas: (e.preguntas || []).map(p => ({
                    id: p.id,
                    texto: p.texto,
                    tipo: p.tipo,
                    obligatoria: p.obligatoria,
                    permiteOtro: !!p.permiteOtro,
                    seccionId: p.seccionId ? String(p.seccionId) : null,
                    // Sin esto la imagen de la pregunta se perdia al editar: no se
                    // cargaba, y al guardar se mandaba imagenUrl en null, borrando la
                    // que ya estaba guardada.
                    imagenUrl: p.imagenUrl || null,
                    opciones: normalizePreguntaOpciones(p.tipo, p.opciones || []),
                    logicaSalto: p.logicaSalto || {},
                    usarSalto: p.logicaSalto && Object.keys(p.logicaSalto).length > 0,
                })),
                empleadosInvitados: (e.empleadosInvitados || []).map(emp => emp.id),
                totalRespuestas: e.totalRespuestas || 0,
            });
            setView('form');
        } catch (err) {
            alert('Error cargando encuesta');
        }
    };

    const handleSaveEncuesta = async () => {
        setSubmitting(true);
        try {
            const payload = {
                titulo: formData.titulo,
                tituloPrivado: (formData.tituloPrivado || '').trim() || null,
                descripcion: formData.descripcion,
                bannerUrl: formData.bannerUrl,
                imagenExtraUrl: formData.imagenExtraUrl,
                activa: formData.activa,
                fechaLimite: formData.fechaLimite || null,
                empleadosInvitados: formData.empleadosInvitados,
                secciones: formData.secciones.map((s, i) => ({
                    id: s.id,
                    clientKey: s.clientKey,
                    titulo: s.titulo,
                    descripcion: s.descripcion,
                    orden: i,
                })),
                preguntas: formData.preguntas.map(p => ({
                    id: p.id,
                    texto: p.texto,
                    tipo: p.tipo,
                    obligatoria: p.obligatoria,
                    permiteOtro: !!p.permiteOtro,
                    seccionId: p.seccionId,
                    imagenUrl: p.imagenUrl || null,
                    opciones: serializePreguntaOpciones(p.tipo, p.opciones),
                    logicaSalto: (p.usarSalto && ['opcion_unica', 'opcion_multiple'].includes(p.tipo)) ? (p.logicaSalto || {}) : {},
                })),
            };

            if (formMode === 'edit' && formData.id) {
                await axiosInstance.put(`/api/encuestas-producto/${formData.id}`, payload);
                setSuccess('Encuesta actualizada');
            } else {
                await axiosInstance.post('/api/encuestas-producto', payload);
                setSuccess('Encuesta creada y notificaciones enviadas');
            }
            setView('list');
            setFormData(EMPTY_FORM);
            fetchEncuestas();
        } catch (err) {
            alert(err.response?.data?.error || 'Error al guardar');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteEncuesta = async (encuesta) => {
        const nombre = nombreGestion(encuesta);
        if (!window.confirm(nombre ? `¿Eliminar la encuesta "${nombre}"?` : '¿Eliminar esta encuesta?')) return;
        try {
            await axiosInstance.delete(`/api/encuestas-producto/${encuesta.id}`);
            fetchEncuestas();
        } catch (err) {
            alert(err.response?.data?.error || 'Error al eliminar');
        }
    };

    const handleDuplicar = async (id) => {
        try {
            await axiosInstance.post(`/api/encuestas-producto/${id}/duplicar`);
            fetchEncuestas();
        } catch (err) { alert(err.response?.data?.error || 'Error al duplicar'); }
    };

    const handleExportExcel = async (id) => {
        try {
            const response = await axiosInstance.get(`/api/encuestas-producto/${id}/resultados/excel`, { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `resultados_encuesta_${id}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) { alert('Error exportando Excel'); }
    };

    const handleImprimir = async (encuesta) => {
        try {
            // El listado no siempre trae las preguntas: pedimos la encuesta completa.
            const res = await axiosInstance.get(`/api/encuestas-producto/${encuesta.id}`);
            const ventana = window.open('', '_blank');
            if (!ventana) {
                alert('Permite las ventanas emergentes para imprimir la encuesta');
                return;
            }
            ventana.document.write(generarHTMLImpresion(res.data));
            ventana.document.close();
        } catch (err) {
            alert('Error al preparar la impresión');
        }
    };

    const [sugiriendoPreguntas, setSugiriendoPreguntas] = useState(false);
    const handleSugerirPreguntas = async () => {
        try {
            setSugiriendoPreguntas(true);
            const res = await axiosInstance.post('/api/encuestas-producto/ai/sugerir-preguntas', {
                titulo: formData.titulo,
                descripcion: formData.descripcion,
            });
            const sugeridas = (res.data.preguntas || []).map(p => ({
                id: null,
                texto: p.texto,
                tipo: p.tipo || 'opcion_unica',
                obligatoria: p.obligatoria !== false,
                permiteOtro: false,
                seccionId: null,
                opciones: normalizePreguntaOpciones(p.tipo || 'opcion_unica', p.opciones || [])
            }));
            setFormData(prev => ({ ...prev, preguntas: [...prev.preguntas, ...sugeridas] }));
        } catch (err) { alert(err.response?.data?.error || 'Error al sugerir'); }
        finally { setSugiriendoPreguntas(false); }
    };

    const addSeccion = () => setFormData(prev => ({
        ...prev,
        secciones: [...prev.secciones, { id: null, clientKey: newClientKey(), titulo: 'Nueva sección', descripcion: '' }]
    }));

    const updateSeccion = (si, field, value) => setFormData(prev => {
        const secciones = [...prev.secciones];
        secciones[si] = { ...secciones[si], [field]: value };
        return { ...prev, secciones };
    });

    const removeSeccion = (si) => setFormData(prev => {
        const sec = prev.secciones[si];
        const keyToRemove = sec.clientKey;
        return {
            ...prev,
            secciones: prev.secciones.filter((_, i) => i !== si),
            preguntas: prev.preguntas.map(p => p.seccionId === keyToRemove ? { ...p, seccionId: null } : p),
        };
    });

    const addPregunta = (seccionId = null) => setFormData(prev => ({
        ...prev,
        preguntas: [...prev.preguntas, {
            id: null, texto: '', tipo: 'opcion_unica', obligatoria: true, permiteOtro: false,
            seccionId,
            imagenUrl: null,
            opciones: [{ texto: '', imagen_url: null }, { texto: '', imagen_url: null }],
            logicaSalto: {},
            usarSalto: false,
        }]
    }));

    const updatePregunta = (pi, field, value) => setFormData(prev => {
        const preguntas = [...prev.preguntas];
        const nextPregunta = { ...preguntas[pi], [field]: value };
        if (field === 'tipo') {
            nextPregunta.permiteOtro = ['opcion_unica', 'opcion_multiple'].includes(value) ? !!nextPregunta.permiteOtro : false;
            nextPregunta.opciones = normalizePreguntaOpciones(value, preguntas[pi].opciones);
            // al cambiar tipo se pierde el salto condicional
            if (!['opcion_unica', 'opcion_multiple'].includes(value)) {
                nextPregunta.usarSalto = false;
                nextPregunta.logicaSalto = {};
            }
        }
        preguntas[pi] = nextPregunta;
        return { ...prev, preguntas };
    });

    const removePregunta = (pi) => setFormData(prev => ({ ...prev, preguntas: prev.preguntas.filter((_, i) => i !== pi) }));

    const movePregunta = (pi, dir) => setFormData(prev => {
        const target = pi + dir;
        if (target < 0 || target >= prev.preguntas.length) return prev;
        const preguntas = [...prev.preguntas];
        [preguntas[pi], preguntas[target]] = [preguntas[target], preguntas[pi]];
        return { ...prev, preguntas };
    });

    const addOpcion = (pi) => setFormData(prev => {
        const preguntas = [...prev.preguntas];
        const pregunta = preguntas[pi];
        if (['matriz', 'matriz_multiple'].includes(pregunta.tipo)) {
            const config = getPreguntaMatrizConfig(pregunta);
            preguntas[pi] = { ...pregunta, opciones: { ...config, criterios: [...config.criterios, { texto: '', imagen_url: null }] } };
        } else {
            const opciones = Array.isArray(pregunta.opciones) ? pregunta.opciones : [];
            preguntas[pi] = { ...pregunta, opciones: [...opciones, { texto: '', imagen_url: null }] };
        }
        return { ...prev, preguntas };
    });

    const addColumna = (pi) => setFormData(prev => {
        const preguntas = [...prev.preguntas];
        const pregunta = preguntas[pi];
        if (['matriz', 'matriz_multiple'].includes(pregunta.tipo)) {
            const config = getPreguntaMatrizConfig(pregunta);
            preguntas[pi] = { ...pregunta, opciones: { ...config, columnas: [...config.columnas, { texto: '', imagen_url: null }] } };
        } else if (pregunta.tipo === 'escala') {
            const columnas = getPreguntaScaleOptions(pregunta);
            preguntas[pi] = { ...pregunta, opciones: [...columnas, { texto: '', imagen_url: null }] };
        }
        return { ...prev, preguntas };
    });

    const updateOpcion = (pi, oi, field, value) => setFormData(prev => {
        const preguntas = [...prev.preguntas];
        const pregunta = preguntas[pi];
        if (['matriz', 'matriz_multiple'].includes(pregunta.tipo)) {
            const config = getPreguntaMatrizConfig(pregunta);
            const criterios = [...config.criterios];
            criterios[oi] = { ...normalizeOpcion(criterios[oi]), [field]: value };
            preguntas[pi] = { ...pregunta, opciones: { ...config, criterios } };
        } else {
            const opciones = [...(Array.isArray(pregunta.opciones) ? pregunta.opciones : [])];
            opciones[oi] = { ...normalizeOpcion(opciones[oi]), [field]: value };
            preguntas[pi] = { ...pregunta, opciones };
        }
        return { ...prev, preguntas };
    });

    const updateColumna = (pi, oi, field, value) => setFormData(prev => {
        const preguntas = [...prev.preguntas];
        const pregunta = preguntas[pi];
        if (['matriz', 'matriz_multiple'].includes(pregunta.tipo)) {
            const config = getPreguntaMatrizConfig(pregunta);
            const columnas = [...config.columnas];
            columnas[oi] = { ...normalizeOpcion(columnas[oi]), [field]: value };
            preguntas[pi] = { ...pregunta, opciones: { ...config, columnas } };
        } else if (pregunta.tipo === 'escala') {
            const columnas = [...getPreguntaScaleOptions(pregunta)];
            columnas[oi] = { ...normalizeOpcion(columnas[oi]), [field]: value };
            preguntas[pi] = { ...pregunta, opciones: columnas };
        }
        return { ...prev, preguntas };
    });

    const removeOpcion = (pi, oi) => setFormData(prev => {
        const preguntas = [...prev.preguntas];
        const pregunta = preguntas[pi];
        if (['matriz', 'matriz_multiple'].includes(pregunta.tipo)) {
            const config = getPreguntaMatrizConfig(pregunta);
            preguntas[pi] = { ...pregunta, opciones: { ...config, criterios: config.criterios.filter((_, i) => i !== oi) } };
        } else {
            preguntas[pi] = { ...pregunta, opciones: (Array.isArray(pregunta.opciones) ? pregunta.opciones : []).filter((_, i) => i !== oi) };
        }
        return { ...prev, preguntas };
    });

    const invertirColumnas = (pi) => {
        // Las respuestas guardan la posición: invertir aquí reinterpreta las que ya existen.
        const yaRespondida = (formData.totalRespuestas || 0) > 0;
        if (yaRespondida && !window.confirm(
            `Esta encuesta ya tiene ${formData.totalRespuestas} respuestas. ` +
            'Invertir las columnas cambia cómo se leen esas respuestas: lo que alguien marcó como la mejor opción pasará a leerse como la peor. ¿Invertir de todos modos?'
        )) return;

        setFormData(prev => {
            const preguntas = [...prev.preguntas];
            const pregunta = preguntas[pi];
            if (['matriz', 'matriz_multiple'].includes(pregunta.tipo)) {
                const config = getPreguntaMatrizConfig(pregunta);
                preguntas[pi] = { ...pregunta, opciones: { ...config, columnas: [...config.columnas].reverse() } };
            } else if (pregunta.tipo === 'escala') {
                preguntas[pi] = { ...pregunta, opciones: [...getPreguntaScaleOptions(pregunta)].reverse() };
            }
            return { ...prev, preguntas };
        });
    };

    const removeColumna = (pi, oi) => setFormData(prev => {
        const preguntas = [...prev.preguntas];
        const pregunta = preguntas[pi];
        if (['matriz', 'matriz_multiple'].includes(pregunta.tipo)) {
            const config = getPreguntaMatrizConfig(pregunta);
            preguntas[pi] = { ...pregunta, opciones: { ...config, columnas: config.columnas.filter((_, i) => i !== oi) } };
        } else if (pregunta.tipo === 'escala') {
            preguntas[pi] = { ...pregunta, opciones: getPreguntaScaleOptions(pregunta).filter((_, i) => i !== oi) };
        }
        return { ...prev, preguntas };
    });

    if (view === 'results') return (
        <div className="max-w-[1400px] mx-auto animate-fadeIn pb-12 pt-4">
            <ResultadosDashboard encuesta={selectedEncuesta} onBack={() => { setView('list'); setSelectedEncuesta(null); }} />
        </div>
    );

    if (view === 'historial') return (
        <div className="max-w-[1400px] mx-auto animate-fadeIn pb-12 pt-4">
            <HistorialEmpleadoView onBack={() => setView('list')} />
        </div>
    );

    if (view === 'form') {
        const canSave = formData.titulo && formData.preguntas.length > 0 && formData.empleadosInvitados.length > 0 && !submitting;
        const closeForm = () => { setView('list'); setFormData(EMPTY_FORM); };
        return (
            <div className="max-w-[1400px] mx-auto animate-fadeIn pb-12 pt-4">
                {/* Sticky top bar */}
                <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-gray-100 -mx-4 px-4 py-3 mb-6 flex items-center gap-3">
                    <button onClick={closeForm} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                        <ArrowLeft size={20} className="text-gray-600" />
                    </button>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FileText size={18} className="text-teal-600 flex-shrink-0" />
                        <h1 className="text-lg font-semibold text-gray-900 truncate">
                            {formMode === 'edit'
                                ? (nombreGestion(formData) ? `Editar: ${nombreGestion(formData)}` : 'Editar encuesta')
                                : 'Nueva encuesta'}
                        </h1>
                    </div>
                    <button onClick={closeForm} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">Cancelar</button>
                    <motion.button onClick={handleSaveEncuesta} disabled={!canSave}
                        whileHover={{ scale: canSave ? 1.02 : 1 }} whileTap={{ scale: canSave ? 0.98 : 1 }}
                        className="flex items-center gap-2 px-5 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors text-sm font-medium disabled:opacity-50 shadow-sm">
                        {submitting ? <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <Save size={16} />}
                        {formMode === 'edit' ? 'Guardar cambios' : 'Crear encuesta'}
                    </motion.button>
                </div>

                <div className="grid lg:grid-cols-[340px_1fr] gap-6">
                    {/* Columna izquierda: metadata */}
                    <div className="space-y-6">
                        <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-4">
                            <label className="block text-sm font-medium text-gray-700">Banner</label>
                            <ImageUploadButton value={formData.bannerUrl} onChange={url => setFormData(prev => ({ ...prev, bannerUrl: url }))} />

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Nombre público * <span className="text-gray-400 font-normal text-xs">(lo verán todos los invitados)</span>
                                </label>
                                <input value={formData.titulo} onChange={e => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
                                    placeholder="Ej: Prueba de uso: Suavizante"
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-500 text-sm" />
                            </div>

                            <div>
                                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                                    <Lock size={12} className="text-gray-400" />
                                    Nombre interno <span className="text-gray-400 font-normal text-xs">(opcional, solo lo ven quienes gestionan encuestas)</span>
                                </label>
                                <input value={formData.tituloPrivado} onChange={e => setFormData(prev => ({ ...prev, tituloPrivado: e.target.value }))}
                                    placeholder="Ej: Dalan suavizante — piloto agosto"
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-500 text-sm" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Descripción</label>
                                <textarea value={formData.descripcion} onChange={e => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                                    placeholder="Descripción breve..." rows={3}
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-500 text-sm resize-none" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Imagen adicional <span className="text-gray-400 font-normal text-xs">(después del encabezado)</span></label>
                                <ImageUploadButton value={formData.imagenExtraUrl} onChange={url => setFormData(prev => ({ ...prev, imagenExtraUrl: url }))} height="h-40" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Fecha límite</label>
                                <input type="datetime-local" value={formData.fechaLimite} onChange={e => setFormData(prev => ({ ...prev, fechaLimite: e.target.value }))}
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-500 text-sm" />
                            </div>

                            <label className="flex items-center gap-2 cursor-pointer pt-1">
                                <div onClick={() => setFormData(prev => ({ ...prev, activa: !prev.activa }))}
                                    className={`w-10 h-6 rounded-full transition-colors flex items-center px-1 ${formData.activa ? 'bg-teal-600' : 'bg-gray-300'}`}>
                                    <motion.div className="w-4 h-4 rounded-full bg-white shadow-sm" animate={{ x: formData.activa ? 16 : 0 }} transition={{ type: 'spring', stiffness: 500, damping: 30 }} />
                                </div>
                                <span className="text-sm text-gray-700">Activa</span>
                            </label>
                        </div>

                        <div className="bg-white border border-gray-100 rounded-2xl p-5">
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                Empleados invitados *
                                {formData.empleadosInvitados.length > 0 && <span className="ml-2 text-teal-600 font-semibold">{formData.empleadosInvitados.length}</span>}
                            </label>
                            <EmpleadoSelector selectedIds={formData.empleadosInvitados} onChange={ids => setFormData(prev => ({ ...prev, empleadosInvitados: ids }))} />
                        </div>
                    </div>

                    {/* Columna derecha: secciones + preguntas */}
                    <div className="space-y-6 min-w-0">
                        {/* Secciones */}
                        <div className="bg-white border border-gray-100 rounded-2xl p-5">
                            <div className="flex items-center justify-between mb-3">
                                <label className="block text-sm font-medium text-gray-700">
                                    Secciones <span className="text-gray-400 font-normal text-xs">(opcional, agrupan preguntas)</span>
                                </label>
                                <button type="button" onClick={addSeccion} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 font-medium">
                                    <Layers size={13} />Añadir sección
                                </button>
                            </div>
                            {formData.secciones.length > 0 ? (
                                <div className="space-y-3">
                                    {formData.secciones.map((s, si) => (
                                        <div key={s.clientKey} className="border border-teal-100 bg-teal-50/40 rounded-xl p-3">
                                            <div className="flex items-center gap-2">
                                                <Layers size={15} className="text-teal-600 flex-shrink-0" />
                                                <input value={s.titulo} onChange={e => updateSeccion(si, 'titulo', e.target.value)}
                                                    placeholder="Título de la sección"
                                                    className="flex-1 px-3 py-1.5 bg-white border border-teal-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-500 font-medium" />
                                                <button onClick={() => removeSeccion(si)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Quitar sección">
                                                    <X size={14} />
                                                </button>
                                            </div>
                                            <input value={s.descripcion || ''} onChange={e => updateSeccion(si, 'descripcion', e.target.value)}
                                                placeholder="Descripción opcional"
                                                className="w-full mt-2 px-3 py-1.5 bg-white border border-teal-100 rounded-lg text-xs outline-none focus:ring-2 focus:ring-teal-500 text-gray-600" />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-xs text-gray-400">Aún no hay secciones. Las preguntas quedarán en un bloque único.</p>
                            )}
                        </div>

                        {/* Preguntas */}
                        <div className="bg-white border border-gray-100 rounded-2xl p-5">
                            <label className="block text-sm font-medium text-gray-700 mb-3">Preguntas *</label>
                            <div className="space-y-4">
                                {formData.preguntas.map((pregunta, pi) => {
                                    const matrizConfig = getPreguntaMatrizConfig(pregunta);
                                    const opcNorm = ['matriz', 'matriz_multiple'].includes(pregunta.tipo)
                                        ? matrizConfig.criterios
                                        : (Array.isArray(pregunta.opciones) ? pregunta.opciones.map(normalizeOpcion) : []);
                                    const escalaNorm = getPreguntaScaleOptions(pregunta);
                                    return (
                                        <div key={pi} className="border border-gray-200 rounded-2xl p-4 space-y-3">
                                            <div className="flex items-center gap-2">
                                                <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 text-xs font-bold flex items-center justify-center flex-shrink-0">{pi + 1}</span>
                                                <input value={pregunta.texto} onChange={e => updatePregunta(pi, 'texto', e.target.value)}
                                                    placeholder="Escribe la pregunta aquí..."
                                                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-500 text-sm" />
                                                {!pregunta.imagenUrl && (
                                                    <ImageUploadButton value={pregunta.imagenUrl} onChange={url => updatePregunta(pi, 'imagenUrl', url)} buttonMode={true} />
                                                )}
                                                {formData.preguntas.length > 1 && (
                                                    <div className="flex items-center flex-shrink-0">
                                                        <button type="button" onClick={() => movePregunta(pi, -1)} disabled={pi === 0} title="Subir"
                                                            className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"><ChevronUp size={16} /></button>
                                                        <button type="button" onClick={() => movePregunta(pi, 1)} disabled={pi === formData.preguntas.length - 1} title="Bajar"
                                                            className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"><ChevronDown size={16} /></button>
                                                    </div>
                                                )}
                                                <button onClick={() => removePregunta(pi)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><X size={16} /></button>
                                            </div>
                                            {pregunta.imagenUrl && (
                                                <div className="mt-1">
                                                    <ImageUploadButton value={pregunta.imagenUrl} onChange={url => updatePregunta(pi, 'imagenUrl', url)} height="h-32" />
                                                </div>
                                            )}
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <select value={pregunta.tipo} onChange={e => updatePregunta(pi, 'tipo', e.target.value)}
                                                    className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-teal-500">
                                                    {TIPOS_PREGUNTA.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                                                </select>
                                                {formData.secciones.length > 0 && (
                                                    <select value={pregunta.seccionId || ''} onChange={e => updatePregunta(pi, 'seccionId', e.target.value || null)}
                                                        className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-teal-500">
                                                        <option value="">Sin sección</option>
                                                        {formData.secciones.map(s => (
                                                            <option key={s.clientKey} value={s.clientKey}>{s.titulo || 'Sección'}</option>
                                                        ))}
                                                    </select>
                                                )}
                                                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                                    <input type="checkbox" checked={pregunta.obligatoria} onChange={e => updatePregunta(pi, 'obligatoria', e.target.checked)} className="accent-teal-600 w-4 h-4" />
                                                    Obligatoria
                                                </label>
                                                {['opcion_unica', 'opcion_multiple'].includes(pregunta.tipo) && (
                                                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                                        <input type="checkbox" checked={!!pregunta.permiteOtro} onChange={e => updatePregunta(pi, 'permiteOtro', e.target.checked)} className="accent-teal-600 w-4 h-4" />
                                                        Opción "Otro"
                                                    </label>
                                                )}
                                                {/* Toggle salto condicional — opción única y múltiple con secciones */}
                                                {['opcion_unica', 'opcion_multiple'].includes(pregunta.tipo) && formData.secciones.length > 0 && (
                                                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer ml-auto">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!pregunta.usarSalto}
                                                            onChange={e => {
                                                                updatePregunta(pi, 'usarSalto', e.target.checked);
                                                                if (!e.target.checked) updatePregunta(pi, 'logicaSalto', {});
                                                            }}
                                                            className="accent-teal-600 w-4 h-4"
                                                        />
                                                        Ir a sección según respuesta
                                                    </label>
                                                )}
                                            </div>
                                            {tieneOpciones(pregunta.tipo) && (
                                                <div className="space-y-2 pt-1">
                                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{['matriz', 'matriz_multiple'].includes(pregunta.tipo) ? 'Criterios' : 'Opciones'}</p>
                                                    {opcNorm.map((op, oi) => (
                                                        <div key={oi} className="flex items-center gap-2">
                                                            <input type="text" value={op.texto} onChange={e => updateOpcion(pi, oi, 'texto', e.target.value)}
                                                                className="flex-1 px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-teal-500"
                                                                placeholder={['matriz', 'matriz_multiple'].includes(pregunta.tipo) ? `Criterio ${oi + 1}` : `Opción ${oi + 1}`} />
                                                            {/* Dropdown de salto por opción */}
                                                            {pregunta.usarSalto && ['opcion_unica', 'opcion_multiple'].includes(pregunta.tipo) && (
                                                                <select
                                                                    value={(pregunta.logicaSalto || {})[op.texto] || ''}
                                                                    onChange={e => {
                                                                        const nuevoSalto = { ...(pregunta.logicaSalto || {}), [op.texto]: e.target.value || null };
                                                                        updatePregunta(pi, 'logicaSalto', nuevoSalto);
                                                                    }}
                                                                    className="px-2 py-2 bg-teal-50 border border-teal-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-teal-500 text-teal-800 min-w-[160px]"
                                                                >
                                                                    <option value="">→ Continuar orden</option>
                                                                    {formData.secciones.map(s => (
                                                                        <option key={s.clientKey} value={s.clientKey}>→ {s.titulo || 'Sección'}</option>
                                                                    ))}
                                                                    <option value="ENVIAR">→ Enviar formulario</option>
                                                                </select>
                                                            )}
                                                            <button onClick={() => removeOpcion(pi, oi)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><X size={15} /></button>
                                                        </div>
                                                    ))}
                                                    <button type="button" onClick={() => addOpcion(pi)} className="flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-700 mt-1">
                                                        <Plus size={15} />{['matriz', 'matriz_multiple'].includes(pregunta.tipo) ? 'Añadir criterio' : 'Añadir opción'}
                                                    </button>
                                                </div>
                                            )}
                                            {pregunta.tipo === 'escala' && (
                                                <ColumnasEditor
                                                    columnas={escalaNorm}
                                                    titulo="Valores de la escala"
                                                    ayuda="De menor a mayor: el último valor es el más alto."
                                                    placeholder="Valor"
                                                    textoAgregar="Añadir valor"
                                                    onUpdate={(oi, campo, valor) => updateColumna(pi, oi, campo, valor)}
                                                    onRemove={oi => removeColumna(pi, oi)}
                                                    onAdd={() => addColumna(pi)}
                                                    onInvertir={() => invertirColumnas(pi)}
                                                />
                                            )}
                                            {['matriz', 'matriz_multiple'].includes(pregunta.tipo) && (
                                                <div className="border-t border-gray-100 mt-3 pt-3">
                                                    <ColumnasEditor
                                                        columnas={matrizConfig.columnas}
                                                        titulo="Columnas de la matriz"
                                                        ayuda="De menor a mayor: la última columna es la calificación más alta."
                                                        placeholder="Columna"
                                                        textoAgregar="Añadir columna"
                                                        onUpdate={(oi, campo, valor) => updateColumna(pi, oi, campo, valor)}
                                                        onRemove={oi => removeColumna(pi, oi)}
                                                        onAdd={() => addColumna(pi)}
                                                        onInvertir={() => invertirColumnas(pi)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {formData.preguntas.length === 0 && (
                                    <div className="text-center py-10 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                                        <p className="text-sm text-gray-400">Sin preguntas. Añade una para comenzar.</p>
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-3 mt-4">
                                <button type="button" onClick={() => addPregunta(null)}
                                    className="flex-1 flex items-center justify-center gap-2 p-4 bg-gray-50 text-gray-600 rounded-xl border-2 border-dashed border-gray-200 hover:border-teal-300 hover:text-teal-600 hover:bg-teal-50 transition-colors text-sm font-medium">
                                    <Plus size={18} />Añadir pregunta
                                </button>
                                <button type="button" onClick={handleSugerirPreguntas} disabled={sugiriendoPreguntas || !formData.titulo}
                                    className="flex items-center justify-center gap-2 px-5 py-3 bg-violet-50 text-violet-700 rounded-xl border-2 border-dashed border-violet-200 hover:border-violet-400 hover:bg-violet-100 transition-colors text-sm font-medium disabled:opacity-50"
                                    title="Sugerir preguntas con IA">
                                    {sugiriendoPreguntas ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                                    Sugerir con IA
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (view === 'success') {
        return (
            <div className="max-w-[1400px] mx-auto animate-fadeIn pb-12 pt-4 flex justify-center items-center h-[70vh]">
                <div className="text-center space-y-4 max-w-md bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
                    <CheckCircle className="mx-auto text-emerald-500 w-16 h-16" />
                    <h2 className="text-2xl font-bold text-gray-800">¡Encuesta enviada!</h2>
                    <p className="text-gray-500 text-sm">Tus respuestas han sido registradas correctamente. Agradecemos tu participación.</p>
                    <div className="flex gap-4 justify-center mt-6">
                        <button onClick={() => setView('list')} className="px-5 py-2.5 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-xl transition-colors font-medium">Volver al inicio</button>
                        {(!selectedEncuesta?.fechaLimite || new Date(selectedEncuesta.fechaLimite) > new Date()) && (
                            <button onClick={() => handleOpenEncuesta(selectedEncuesta)} className="px-5 py-2.5 bg-teal-600 text-white hover:bg-teal-700 rounded-xl transition-colors font-medium">Modificar respuesta</button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (view === 'respond') {
        const preguntasActivas = (selectedEncuesta?.preguntas || []).filter(p => !p.archivada);
        const secciones = selectedEncuesta?.secciones || [];
        const tieneSecciones = secciones.length > 0;

        // Calcular los grupos visibles DINÁMICAMENTE según las respuestas actuales
        const { gruposVisibles } = calcularVisibilidadEncuesta(selectedEncuesta, respuestas);

        // Agregamos las preguntas al grupo
        const todosLosGroups = gruposVisibles.map(g => ({
            ...g,
            preguntas: preguntasActivas.filter(p => 
                g.seccion ? String(p.seccionId) === String(g.seccion.id) : !p.seccionId
            )
        }));

        // ── Si NO hay secciones: comportamiento original (todo junto) ──────────
        if (!tieneSecciones) {
            return (
                <div className="max-w-[1400px] mx-auto animate-fadeIn pb-12 pt-4">
                    <div className="max-w-3xl mx-auto space-y-6">
                        {selectedEncuesta?.bannerUrl && (
                            <div className="w-full h-48 rounded-2xl overflow-hidden">
                                <img src={selectedEncuesta.bannerUrl} alt="banner" className="w-full h-full object-cover" />
                            </div>
                        )}
                        <div className="flex items-center gap-4">
                            <button onClick={() => setView('list')} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                                <ArrowLeft size={20} className="text-gray-600" />
                            </button>
                            <div className="flex-1">
                                <h1 className="text-2xl font-bold text-gray-900 leading-tight">{selectedEncuesta?.titulo}</h1>
                                {selectedEncuesta?.descripcion && <p className="text-gray-500 mt-0.5 whitespace-pre-wrap">{selectedEncuesta.descripcion}</p>}
                            </div>
                        </div>
                        {selectedEncuesta?.imagenExtraUrl && (
                            <div className="w-full rounded-2xl overflow-hidden border border-gray-100">
                                <img src={selectedEncuesta.imagenExtraUrl} alt="" className="w-full max-h-[500px] object-contain bg-gray-50" />
                            </div>
                        )}
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl flex items-center gap-3">
                                <AlertCircle size={18} />{error}
                            </div>
                        )}
                        <div className="space-y-4">
                            {todosLosGroups.map((g, gi) => (
                                g.preguntas.length > 0 && (
                                    <React.Fragment key={g.key}>
                                        {g.seccion && <SeccionHeader seccion={g.seccion} />}
                                        {g.preguntas.map(pregunta => (
                                            <PreguntaRespuesta
                                                key={pregunta.id}
                                                pregunta={pregunta}
                                                value={respuestas[pregunta.id]}
                                                onChange={val => setRespuestas(prev => ({ ...prev, [pregunta.id]: val }))}
                                            />
                                        ))}
                                    </React.Fragment>
                                )
                            ))}
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <button onClick={() => setView('list')} className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">Cancelar</button>
                            <motion.button onClick={handleSubmitRespuesta} disabled={submitting}
                                className="flex items-center gap-2 px-6 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50"
                                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                {submitting ? <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <Send size={16} />}
                                Enviar respuestas
                            </motion.button>
                        </div>
                    </div>
                </div>
            );
        }

        // ── Con secciones: navegación PASO A PASO ─────────────────────────────
        const grupoActual = todosLosGroups[seccionActualIdx] || todosLosGroups[0];
        const esUltimoGrupo = seccionActualIdx >= todosLosGroups.length - 1;

        const handleSiguiente = () => {
            // Validar preguntas obligatorias del grupo actual
            setError('');
            const preguntas = grupoActual?.preguntas || [];
            for (const p of preguntas) {
                if (!p.obligatoria) continue;
                const val = respuestas[p.id];
                if (['matriz', 'matriz_multiple'].includes(p.tipo)) {
                    if (isMatrizVacia(p, val)) {
                        setError(`La pregunta "${p.texto.slice(0, 60)}..." es obligatoria`);
                        return;
                    }
                    continue;
                }
                const isEmpty = val === undefined || val === null || val === '' ||
                    (Array.isArray(val) && val.length === 0);
                if (isEmpty && val !== 0) {
                    setError(`La pregunta "${p.texto.slice(0, 60)}..." es obligatoria`);
                    return;
                }
            }

            // Al darle siguiente, el flujo natural es ir al +1 en los grupos VISIBLES.
            // Si el siguiente paso no existe, es porque enviar respuestas cortó el flujo.
            if (!esUltimoGrupo) {
                setHistorialNav(prev => [...prev, seccionActualIdx]);
                setSeccionActualIdx(prev => prev + 1);
            } else {
                handleSubmitRespuesta();
            }
        };

        const handleAnterior = () => {
            setError('');
            if (historialNav.length > 0) {
                const anterior = historialNav[historialNav.length - 1];
                setHistorialNav(prev => prev.slice(0, -1));
                setSeccionActualIdx(anterior);
            }
        };

        // Progreso visual
        const totalGrupos = todosLosGroups.length;
        const progreso = Math.round(((seccionActualIdx + 1) / totalGrupos) * 100);
        const mostrarEnviar = esUltimoGrupo;

        return (
            <div className="max-w-[1400px] mx-auto animate-fadeIn pb-12 pt-4">
                <div className="max-w-3xl mx-auto space-y-6">
                    {/* Banner */}
                    {selectedEncuesta?.bannerUrl && (
                        <div className="w-full h-48 rounded-2xl overflow-hidden">
                            <img src={selectedEncuesta.bannerUrl} alt="banner" className="w-full h-full object-cover" />
                        </div>
                    )}

                    {/* Encabezado */}
                    <div className="flex items-center gap-4">
                        <button onClick={() => setView('list')} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                            <ArrowLeft size={20} className="text-gray-600" />
                        </button>
                        <div className="flex-1">
                            <h1 className="text-xl font-bold text-gray-900 leading-tight">{selectedEncuesta?.titulo}</h1>
                            {selectedEncuesta?.descripcion && <p className="text-gray-500 mt-0.5 text-sm whitespace-pre-wrap">{selectedEncuesta.descripcion}</p>}
                        </div>
                    </div>

                    {/* Barra de progreso */}
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-600">
                                {grupoActual?.seccion ? grupoActual.seccion.titulo : 'Preguntas generales'}
                            </span>
                            <span className="text-sm font-semibold text-teal-600">
                                {seccionActualIdx + 1} / {totalGrupos}
                            </span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <motion.div
                                className="h-full bg-teal-500 rounded-full"
                                initial={{ width: 0 }}
                                animate={{ width: `${progreso}%` }}
                                transition={{ duration: 0.4, ease: 'easeOut' }}
                            />
                        </div>
                    </div>

                    {/* Imagen adicional (solo en primer grupo) */}
                    {seccionActualIdx === 0 && selectedEncuesta?.imagenExtraUrl && (
                        <div className="w-full rounded-2xl overflow-hidden border border-gray-100">
                            <img src={selectedEncuesta.imagenExtraUrl} alt="" className="w-full max-h-[400px] object-contain bg-gray-50" />
                        </div>
                    )}

                    {/* Error */}
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                            className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl flex items-center gap-3">
                            <AlertCircle size={18} />{error}
                        </motion.div>
                    )}

                    {/* Preguntas de la sección actual */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={seccionActualIdx}
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -30 }}
                            transition={{ duration: 0.3, ease: 'easeOut' }}
                            className="space-y-4"
                        >
                            {grupoActual?.seccion && <SeccionHeader seccion={grupoActual.seccion} />}
                            {(grupoActual?.preguntas || []).map(pregunta => (
                                <PreguntaRespuesta
                                    key={pregunta.id}
                                    pregunta={pregunta}
                                    value={respuestas[pregunta.id]}
                                    onChange={val => setRespuestas(prev => ({ ...prev, [pregunta.id]: val }))}
                                />
                            ))}
                        </motion.div>
                    </AnimatePresence>

                    {/* Navegación */}
                    <div className="flex items-center justify-between pt-2">
                        <button
                            onClick={handleAnterior}
                            disabled={historialNav.length === 0}
                            className="flex items-center gap-2 px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ArrowLeft size={16} />
                            Anterior
                        </button>
                        <motion.button
                            onClick={mostrarEnviar ? handleSubmitRespuesta : handleSiguiente}
                            disabled={submitting}
                            className={`flex items-center gap-2 px-6 py-2.5 text-white rounded-xl transition-colors disabled:opacity-50 font-medium ${mostrarEnviar ? 'bg-teal-600 hover:bg-teal-700' : 'bg-teal-600 hover:bg-teal-700'}`}
                            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        >
                            {submitting
                                ? <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                : mostrarEnviar ? <Send size={16} /> : <ChevronRight size={16} />
                            }
                            {mostrarEnviar ? 'Enviar respuestas' : 'Siguiente'}
                        </motion.button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-[1400px] mx-auto animate-fadeIn pb-12 pt-4">
            <div className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <h2 className="page-title">Encuestas de Productos</h2>
                    {canManage && (
                        <div className="flex items-center gap-3">
                            <motion.button onClick={() => setView('historial')}
                                className="flex items-center gap-2 px-5 py-2.5 bg-white text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
                                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                <History size={18} />Historial por usuario
                            </motion.button>
                            <motion.button onClick={openCreateForm}
                                className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors shadow-sm"
                                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                <Plus size={18} />Nueva encuesta
                            </motion.button>
                        </div>
                    )}
                </div>

                <AnimatePresence>
                    {success && (
                        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                            className="bg-emerald-50 border border-emerald-200 text-emerald-700 p-4 rounded-xl flex items-center gap-3">
                            <CheckCircle size={18} />{success}
                            <button onClick={() => setSuccess('')} className="ml-auto"><X size={16} /></button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {!loading && encuestas.length > 0 && (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col lg:flex-row lg:items-center gap-3">
                        <div className="relative flex-1 min-w-0">
                            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text" value={busqueda} onChange={e => setBusqueda(e.target.value)}
                                placeholder={canManage ? 'Buscar por nombre público o interno...' : 'Buscar encuesta por nombre...'}
                                className="w-full pl-11 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                            />
                            {busqueda && (
                                <button onClick={() => setBusqueda('')} title="Limpiar búsqueda"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            {FILTROS_ESTADO.map(f => {
                                const activo = filtroEstado === f.id;
                                return (
                                    <button key={f.id} onClick={() => setFiltroEstado(f.id)}
                                        className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${activo
                                            ? 'bg-teal-600 text-white border-teal-600'
                                            : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}>
                                        {f.nombre}
                                        <span className={`px-1.5 rounded-full text-xs font-semibold ${activo ? 'bg-white/25 text-white' : 'bg-white text-gray-500 border border-gray-200'}`}>
                                            {conteosEstado[f.id] || 0}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-16"><div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full mx-auto" /></div>
                ) : encuestas.length === 0 ? (
                    <div className="text-center py-16 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                        <ClipboardList size={48} className="text-gray-300 mx-auto mb-3" />
                        <h3 className="text-lg font-semibold text-gray-700 mb-1">Sin encuestas disponibles</h3>
                        <p className="text-gray-400 text-sm">Las encuestas aparecerán aquí cuando estén disponibles</p>
                    </div>
                ) : encuestasVisibles.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                        <Search size={36} className="text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-600 text-sm font-medium">
                            {busqueda ? 'No se encontraron encuestas con ese nombre' : 'No hay encuestas en este estado'}
                        </p>
                        <p className="text-gray-400 text-xs mt-1">Prueba con otra palabra o cambia el filtro</p>
                        <button onClick={() => { setBusqueda(''); setFiltroEstado('todas'); }}
                            className="mt-4 px-4 py-2 text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-xl transition-colors">
                            Limpiar filtros
                        </button>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {encuestasVisibles.map((encuesta, index) => (
                            <motion.div key={encuesta.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }}
                                className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                                <div className="flex gap-5 p-5">
                                    {encuesta.bannerUrl && (
                                        <div className="w-24 h-20 rounded-xl overflow-hidden flex-shrink-0">
                                            <img src={encuesta.bannerUrl} alt="" className="w-full h-full object-cover" />
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                {canManage && encuesta.tituloPrivado ? (
                                                    <>
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <h3 className="font-semibold text-gray-900">{encuesta.tituloPrivado}</h3>
                                                            <span className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-400 text-[10px] font-medium rounded-md uppercase tracking-wide">
                                                                <Lock size={9} />interno
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-gray-400 mt-0.5 truncate" title={encuesta.titulo}>{encuesta.titulo}</p>
                                                    </>
                                                ) : (
                                                    <h3 className="font-semibold text-gray-900">{encuesta.titulo}</h3>
                                                )}
                                                {encuesta.descripcion && <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{encuesta.descripcion}</p>}
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <ChipEstado estado={encuesta.estado} diasRestantes={encuesta.diasRestantes} invitado={encuesta.estaInvitado !== false} />
                                                {!encuesta.activa && canManage && (
                                                    <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                                                        <Lock size={12} />Inactiva
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4 mt-3 text-sm text-gray-400">
                                            <span className="flex items-center gap-1.5"><Calendar size={13} />{new Date(encuesta.fechaCreacion).toLocaleDateString('es-VE')}</span>
                                            <span className="flex items-center gap-1.5"><Users size={13} />{encuesta.totalRespuestas}/{encuesta.totalInvitados}</span>
                                            {encuesta.fechaLimite && (
                                                <span className="flex items-center gap-1.5 text-amber-500">
                                                    <AlertCircle size={13} />Hasta {new Date(encuesta.fechaLimite).toLocaleDateString('es-VE')}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 mt-4 flex-wrap">
                                            {!encuesta.yaRespondio && encuesta.activa && encuesta.estaInvitado && (!encuesta.fechaLimite || new Date(encuesta.fechaLimite) > new Date()) && (
                                                <motion.button onClick={() => handleOpenEncuesta(encuesta)}
                                                    className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors text-sm font-medium"
                                                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                                    Responder <ChevronRight size={15} />
                                                </motion.button>
                                            )}
                                            {encuesta.yaRespondio && encuesta.activa && encuesta.estaInvitado && (!encuesta.fechaLimite || new Date(encuesta.fechaLimite) > new Date()) && (
                                                <motion.button onClick={() => handleOpenEncuesta(encuesta)}
                                                    className="flex items-center gap-2 px-4 py-2 border border-teal-600 text-teal-600 rounded-xl hover:bg-teal-50 transition-colors text-sm font-medium"
                                                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                                    <Edit2 size={15} /> Modificar respuesta
                                                </motion.button>
                                            )}
                                            {/* El estado (incluida "Expirada") ya sale como chip en el encabezado de la tarjeta */}
                                            {canManage && (
                                                <>
                                                    <button onClick={() => { setSelectedEncuesta(encuesta); setView('results'); }}
                                                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors text-sm">
                                                        <BarChart3 size={15} />Resultados
                                                    </button>
                                                    <button onClick={() => handleExportExcel(encuesta.id)}
                                                        className="flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-xl hover:bg-emerald-100 transition-colors text-sm" title="Exportar Excel">
                                                        <Download size={15} />Excel
                                                    </button>
                                                    <button onClick={() => handleImprimir(encuesta)}
                                                        className="flex items-center gap-2 px-3 py-2 bg-sky-50 text-sky-700 rounded-xl hover:bg-sky-100 transition-colors text-sm" title="Imprimir encuesta en blanco para llenar en físico">
                                                        <Printer size={15} />Imprimir
                                                    </button>
                                                    <button onClick={() => openEditForm(encuesta)}
                                                        className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-xl transition-colors" title="Editar encuesta">
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button onClick={() => handleDuplicar(encuesta.id)}
                                                        className="p-2 text-gray-400 hover:bg-gray-100 rounded-xl transition-colors" title="Duplicar encuesta">
                                                        <Copy size={16} />
                                                    </button>
                                                    <button onClick={() => handleDeleteEncuesta(encuesta)} className="p-2 text-red-400 hover:bg-red-50 rounded-xl transition-colors" title="Eliminar">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EncuestasProductoView;
